<template>
    <div v-if="authStore.isAuthenticated">
        <ConfirmModal />
        <div v-if="loading && !event">Chargement...</div>
        <div v-else-if="canManage || canEditLocalInfo">
            <div v-if="error" class="error">{{ error }}</div>

            <div v-if="event" class="module">
                <h2 class="page-title">Inscriptions {{ event.display_name || event.name }} {{ event.place }}</h2>

                <div class="event-banner">
                    <p v-if="event.date"><span class="icon">📅</span> {{ formatEventDateRange(event.date, event.date_end) }}</p>
                    <p v-if="event.place"><span class="icon">📍</span> {{ event.place }}</p>
                    <p v-if="event.organiser"><span class="icon">🏆</span> {{ event.organiser }}</p>
                </div>

                <div class="tabs-bar">
                    <button v-for="tab in visibleTabs" class="tab-btn" :class="{ active: activeTab === tab.id }"
                        type="button" @click="activeTab = tab.id" :key="tab.id">
                        {{ tab.label }}
                    </button>
                </div>

            <div v-show="activeTab === 'inscriptions'">
                <!-- Totaux par catégorie -->
                <div v-if="inscriptions.length" class="stats-section">
                    <p class="stats-section-label">Inscriptions</p>
                    <input v-model="globalSearch" class="global-search-input"
                        placeholder="Rechercher un joueur (toutes catégories)..." />
                    <div class="stats-grid">
                        <div class="stat-card grand-total">
                            <span class="stat-label">Total joueurs</span>
                            <span class="stat-value">{{ uniquePlayerCount }}</span>
                        </div>
                        <div v-for="cat in visibleCategories" class="stat-card" :key="cat.value">
                            <span class="stat-label">{{ cat.short }}</span>
                            <span class="stat-value">{{ getByCategory(cat).length }}</span>
                        </div>
                    </div>
                </div>

                <!-- Sections par catégorie -->
                <div v-for="cat in visibleCategories" class="category-section" :key="cat.value">
                    <h3 class="category-title" @click="categoryCollapsed[cat.value] = !categoryCollapsed[cat.value]">
                        {{ cat.label }}
                        <span class="cat-count">({{ getFilteredByCategory(cat).length }})</span>
                        <span class="cat-toggle">{{ isCategoryOpen(cat) ? '▼' : '▶' }}</span>
                    </h3>

                    <template v-if="isCategoryOpen(cat)">

                        <div v-if="getFilteredByCategory(cat).length > 0">
                            <div class="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th v-if="!cat.isDouble">Joueur</th>
                                            <th v-if="cat.isDouble">Doublette</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="ins in getFilteredByCategory(cat)" :key="ins.id">
                                            <td v-if="!cat.isDouble">{{ getPlayerLabel(ins) }}</td>
                                            <template v-if="cat.isDouble">
                                                <td>{{ getDoubletteLabel(ins) }}</td>
                                            </template>
                                            <td v-if="canManage" class="actions-cell">
                                                <button v-if="cat.isDouble" class="custom-btn"
                                                    title="Supprimer la doublette (les 2 joueurs)"
                                                    @click="deleteDoublette(ins.id, cat.value)">✖️</button>
                                                <template v-else-if="changingCategory?.id === ins.id && changingCategory.from === cat.value">
                                                    <select v-model="changingCategory.to" class="category-change-select"
                                                        aria-label="Nouvelle catégorie">
                                                        <option v-for="t in categoryTargets(ins, cat.value)" :key="t.value"
                                                            :value="t.value">{{ t.short }}</option>
                                                    </select>
                                                    <button class="custom-btn" title="Valider le changement de catégorie"
                                                        :disabled="!changingCategory.to || changingCategorySaving"
                                                        @click="submitCategoryChange">✔️</button>
                                                    <button class="custom-btn" title="Annuler"
                                                        @click="changingCategory = null">↩️</button>
                                                </template>
                                                <template v-else>
                                                    <button v-if="categoryTargets(ins, cat.value).length" class="custom-btn"
                                                        title="Changer de catégorie (inscription et paiement conservés)"
                                                        @click="startCategoryChange(ins, cat.value)">🔁</button>
                                                    <button class="custom-btn" title="Supprimer cette inscription"
                                                        @click="deleteInscription(ins.id, cat.value)">✖️</button>
                                                </template>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="mobile-cards">
                                <div v-for="ins in getFilteredByCategory(cat)" class="mobile-card" :key="'card-' + ins.id">
                                    <div class="card-top">
                                        <span v-if="!cat.isDouble" class="card-name">{{ getPlayerLabel(ins) }}</span>
                                        <span v-else class="card-name">{{ getDoubletteLabel(ins) }}</span>
                                    </div>
                                    <div v-if="canManage" class="card-actions">
                                        <button v-if="cat.isDouble" class="custom-btn"
                                            title="Supprimer la doublette (les 2 joueurs)"
                                            @click="deleteDoublette(ins.id, cat.value)">✖️ Supprimer la doublette</button>
                                        <template v-else-if="changingCategory?.id === ins.id && changingCategory.from === cat.value">
                                            <select v-model="changingCategory.to" class="category-change-select"
                                                aria-label="Nouvelle catégorie">
                                                <option v-for="t in categoryTargets(ins, cat.value)" :key="t.value"
                                                    :value="t.value">{{ t.short }}</option>
                                            </select>
                                            <button class="custom-btn" :disabled="!changingCategory.to || changingCategorySaving"
                                                @click="submitCategoryChange">✔️ Valider</button>
                                            <button class="custom-btn" @click="changingCategory = null">↩️ Annuler</button>
                                        </template>
                                        <template v-else>
                                            <button v-if="categoryTargets(ins, cat.value).length" class="custom-btn"
                                                title="Changer de catégorie (inscription et paiement conservés)"
                                                @click="startCategoryChange(ins, cat.value)">🔁 Changer de catégorie</button>
                                            <button class="custom-btn" title="Supprimer cette inscription"
                                                @click="deleteInscription(ins.id, cat.value)">✖️ Supprimer</button>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-else-if="getByCategory(cat).length === 0" class="no-data">
                            Aucune inscription dans cette catégorie.
                        </p>
                        <p v-else class="no-data">
                            Aucun résultat pour le filtre sélectionné.
                        </p>
                    </template>
                </div>

                <div v-if="canManage" class="global-actions">
                    <button class="btn-secondary" @click="showAddForm = !showAddForm">
                        {{ showAddForm ? 'Fermer' : '+ Ajouter une inscription' }}
                    </button>
                    <button v-if="isSuperAdmin" class="btn-secondary" @click="importSource = 'helloasso'">
                        Importer un export HelloAsso
                    </button>
                    <button v-if="isSuperAdmin" class="btn-secondary" @click="importSource = 'ffd'">
                        Importer une liste FFD
                    </button>
                    <button v-if="isSuperAdmin"  class="btn-secondary" :disabled="normalizing" @click="normalizeAllNames">
                        {{ normalizing ? 'Normalisation...' : 'Normaliser les noms' }}
                    </button>
                    <button v-if="isSuperAdmin" class="btn-danger" @click="confirmDeleteAll">
                        Supprimer toutes les inscriptions
                    </button>
                </div>

                <HelloAssoImportModal v-if="isSuperAdmin && importSource" :eventId="eventId" :source="importSource"
                    @close="importSource = null" @imported="onImported" />

                <!-- Formulaire ajout inscription -->
                <div v-if="showAddForm" class="module add-form">
                    <h4>Ajouter une inscription manuellement</h4>
                    <p class="add-form-hint">
                        Exception hors HelloAsso : l'inscription est immédiatement considérée payée, sous votre
                        responsabilité (paiement reçu par un autre canal, joueur ajouté après coup).
                    </p>

                    <div v-for="(player, index) in adminPlayers" class="player-entry" :key="index">
                        <div class="player-entry-header">
                            <h4>Joueur {{ index + 1 }}</h4>
                            <button v-if="adminPlayers.length > 1" class="btn-remove"
                                @click="adminPlayers.splice(index, 1)">
                                Supprimer
                            </button>
                        </div>

                        <PlayerPicker
                            :name="player.name"
                            :firstname="player.firstname"
                            remote
                            :remoteResults="playerSuggestions[index]"
                            @update:name="player.name = $event; checkPlayerLicense(player, index)"
                            @update:firstname="player.firstname = formatFirstname($event); checkPlayerLicense(player, index)"
                            @search="onPlayerSearch(index, $event)"
                            @pick="(entry) => pickPlayerLicense(player, entry)"
                        />
                        <label class="checkbox-label">
                            <input v-model="player.is_licensed" type="checkbox" /> Licencié
                        </label>
                        <p v-if="event?.cup_scope && !player.is_licensed" class="helloasso-missing-hint">
                            ⚠️ Coupe : un non licencié ne peut pas être inscrit.
                        </p>

                        <!-- Genre : nécessaire pour vérifier l'éligibilité aux catégories
                        individuelles (Masculin, Vétéran, Féminin, Junior) quand le joueur n'est
                        pas licencié. Date de naissance : utile seulement pour Vétéran/Junior (cf.
                        _calculate_category côté backend) -- sans elle, un non-licencié reste
                        éligible à Masculin/Féminin. Les deux sont pré-remplis et verrouillés dès
                        qu'une licence a été trouvée ci-dessus. -->
                        <div class="form-row">
                            <div class="form-group">
                                <label>Date de naissance{{ (player.category_simple || '').split(',').map(c => c.trim()).some(c => c === 'V' || c === 'J') ? ' *' : ' (si Vétéran/Junior)' }}</label>
                                <input v-model="player.birth_date" type="date" class="form-input"
                                    :disabled="!!player.licenseCategory" />
                            </div>
                            <div class="form-group">
                                <label>Genre{{ (player.category_simple || '').split(',').map(c => c.trim()).some(c => c === 'F' || (c === 'M' && !isLocal)) || player.category_double === 'DF' ? ' *' : '' }}</label>
                                <select v-model="player.gender" class="form-input"
                                    :disabled="!!player.licenseCategory">
                                    <option value="">--</option>
                                    <option value="masculin">Masculin</option>
                                    <option value="féminin">Féminin</option>
                                </select>
                            </div>
                        </div>

                        <CategorySelector v-if="openSimpleCategories.length" v-model="player.category_simple"
                            :categories="openSimpleCategories" label="Catégorie individuelle" :multiple="true"
                            :ineligibleReasons="blockedSimpleCategories(player)"
                            :isCumulable="simpleCumulable(player)" />
                        <p v-if="openSimpleCategories.length" class="cat-hint">
                            Une seule catégorie individuelle (sauf junior : Masculin en plus pour un garçon,
                            Féminin en plus pour une fille).
                        </p>

                        <CategorySelector v-if="openDoubleCategories.length" v-model="player.category_double"
                            :categories="openDoubleCategories" label="Catégorie double" :multiple="false"
                            :ineligibleReasons="blockedDoubleCategories(player)" />

                        <div v-if="player.category_double" class="form-group doublette-group">
                            <label>{{ adminHasDoubleDoublette(player) ? 'Partenaire 1 (' +
                                adminGetFirstDoubleCat(player) + ')' :
                                'Nom et prénom du partenaire' }}</label>
                            <PlayerPicker
                                :name="player.partner_name"
                                :firstname="player.partner_firstname"
                                remote
                                :remoteResults="partnerSuggestions[index]"
                                @update:name="player.partner_name = $event; checkPartnerLicense(player, index)"
                                @update:firstname="player.partner_firstname = formatFirstname($event); checkPartnerLicense(player, index)"
                                @search="onPartnerSearch(index, $event)"
                                @pick="(entry) => pickPartnerLicense(player, entry)"
                            />
                            <label class="checkbox-label">
                                <input v-model="player.partner_is_licensed" type="checkbox" /> Partenaire licencié
                            </label>
                            <!-- Nécessaire pour vérifier l'éligibilité à la Double Féminine
                            (réservée aux femmes, joueur ET partenaire). -->
                            <div class="form-group">
                                <label>Genre du partenaire *</label>
                                <select v-model="player.partner_gender" class="form-input">
                                    <option value="">--</option>
                                    <option value="masculin">Masculin</option>
                                    <option value="féminin">Féminin</option>
                                </select>
                            </div>
                        </div>

                        <div v-if="adminHasDoubleDoublette(player)" class="form-group doublette-group">
                            <label>Partenaire 2 ({{ adminGetSecondDoubleCat(player) }})</label>
                            <PlayerPicker
                                :name="player.partner_name_2"
                                :firstname="player.partner_firstname_2"
                                remote
                                :remoteResults="partner2Suggestions[index]"
                                @update:name="player.partner_name_2 = $event; checkPartner2License(player, index)"
                                @update:firstname="player.partner_firstname_2 = formatFirstname($event); checkPartner2License(player, index)"
                                @search="onPartner2Search(index, $event)"
                                @pick="(entry) => pickPartner2License(player, entry)"
                            />
                            <label class="checkbox-label">
                                <input v-model="player.partner_is_licensed_2" type="checkbox" /> Partenaire 2 licencié
                            </label>
                        </div>

                        <div class="player-price">
                            Tarif estimé : <strong>{{ getAdminPlayerPrice(player) }} €</strong>
                        </div>
                    </div>

                    <button class="btn-add-player" @click="adminPlayers.push(createEmptyAdminPlayer())">+ Ajouter un
                        joueur</button>

                    <div class="total-price">
                        <span>Total estimé :</span>
                        <strong>{{ adminTotalPrice }} €</strong>
                    </div>

                    <div class="form-actions">
                        <button class="btn-submit" :disabled="submitting || !adminPlayersHaveAllowedCategory"
                            :title="adminPlayersHaveAllowedCategory ? undefined : 'Choisissez une catégorie disponible pour chaque joueur'"
                            @click="addInscription">
                            {{ submitting ? 'Ajout...' : 'Valider les inscriptions' }}
                        </button>
                        <button class="btn-secondary" @click="showAddForm = false">Annuler</button>
                    </div>
                </div>
            </div>

            <!-- Onglet Participants : event miroir d'un tournoi Badarts, lecture seule --
            les inscriptions/le pointage restent gérés sur Badarts, cf. isLinkedToBadarts. -->
            <div v-show="activeTab === 'roster'">
                <div v-if="badartsRoster.length" class="stats-section">
                    <p class="stats-section-label">Participants ({{ badartsRoster.length }})</p>
                    <button class="btn-export-all" @click="openExport(false)">⬇ Exporter les participants</button>
                </div>
                <p v-if="badartsRosterLoading">Chargement des inscrits...</p>
                <p v-else-if="!badartsRoster.length">Aucun inscrit pour le moment.</p>
                <table v-else class="roster-table">
                    <thead>
                        <tr>
                            <th>Joueur</th>
                            <th v-if="badartsMode === 'double'">Partenaire</th>
                            <th>Payé</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in badartsRoster" :key="p.id">
                            <td>{{ [p.firstname, p.name].filter(Boolean).join(' ') || p.nickname || '' }}</td>
                            <td v-if="badartsMode === 'double'">
                                {{ badartsPairs.find(pair => pair[0].id === p.id || pair[1].id === p.id)
                                    ?.filter(m => m.id !== p.id)
                                    .map(m => `${m.firstname} ${m.name}`)[0] || '' }}
                            </td>
                            <td>{{ p.paid ? '✅' : '' }}</td>
                        </tr>
                    </tbody>
                </table>
                <p class="info-banner">
                    Les inscriptions et le pointage de ce tournoi sont gérés directement sur Badarts.
                    Cette liste est en lecture seule.
                </p>
            </div>

            <!-- Onglet Pointage -->
            <div v-show="activeTab === 'pointage'">
                <div v-if="playerDebts.length" class="category-section player-debts-section">
                    <h3 class="category-title">Pointage</h3>

                    <!-- Ce qu'on pointe : un tournoi sur plusieurs jours pointe les individuels
                    un jour et les doubles le lendemain -- chaque catégorie a son propre
                    pointage (cf. Inscription.checked_in_categories côté backend). -->
                    <div class="pointage-scope">
                        <span class="pointage-scope-label">Je pointe :</span>
                        <div class="pointage-scope-presets">
                            <button v-if="pointableSimples.length" class="status-btn" type="button"
                                :class="{ active: pointageMode === 'simple' }" @click="setPointageKind('simple')">
                                Individuels
                            </button>
                            <button v-if="pointableDoubles.length" class="status-btn" type="button"
                                :class="{ active: pointageMode === 'double' }" @click="setPointageKind('double')">
                                Doubles
                            </button>
                        </div>
                    </div>

                    <div class="filters">
                        <input v-model="search" class="search-input" placeholder="Rechercher un joueur..." />
                        <select v-model="pointageFilter" class="form-input pointage-filter">
                            <option value="all">Tous</option>
                            <option value="unchecked">Non pointés</option>
                            <option value="checked">Pointés</option>
                            <option v-if="hasOnsitePayments" value="onsite_due">À encaisser</option>
                        </select>
                    </div>
                    <div class="checkin-bar">
                        <span class="checkin-counter">
                            {{ pointageMode === 'double' ? 'Équipes pointées' : 'Pointés' }} : {{ pointageRowCheckedCount }} / {{ pointageRowCount }}
                        </span>
                        <span v-if="hasOnsitePayments" class="checkin-counter">
                            Reste à encaisser sur place : {{ totalOnsiteDue }} €
                        </span>
                        <button class="btn-export-all" @click="openExport(false)">⬇ Exporter les participants</button>
                        <button v-if="isSuperAdmin" class="btn-export-all" @click="openExport(true)">⬇ Exporter tous les inscrits (sans pointage)</button>
                    </div>
                    <p v-if="!pointageScope.length" class="no-data">Aucun joueur à pointer pour l'instant.</p>
                    <p v-else-if="!filteredPlayerDebts.length" class="no-data">Aucun joueur pour ce filtre.</p>
                    <template v-else>
                        <div class="table-wrapper">
                            <table class="payments-table">
                                <thead>
                                    <tr>
                                        <th class="checkin-col">Présent</th>
                                        <th>{{ pointageMode === 'double' ? 'Équipe' : 'Joueur' }}</th>
                                        <th class="hideonmobile">Catégorie(s)</th>
                                        <th v-if="hasOnsitePayments">Sur place</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="group in filteredPlayerDebts" class="clickable-row"
                                        :class="{ 'row-checkedin': rowStatus(group) === 'full' }"
                                        :key="group.paymentKey">

                                        <!-- Pointage : clic direct, pas de modale -- pointe d'un coup
                                        toutes les catégories sélectionnées du joueur (des deux
                                        joueurs de l'équipe en mode Doubles). -->
                                        <td class="checkin-col">
                                            <button class="checkin-toggle" :disabled="!canManage"
                                                :title="rowStatus(group) === 'partial' ? 'Pointé dans une partie des catégories sélectionnées' : ''"
                                                @click="canManage && toggleCheckIn(group)">
                                                <span class="checkin-indicator">{{ CHECKIN_ICONS[rowStatus(group)] }}</span>
                                            </button>
                                        </td>

                                        <!-- Nom -->
                                        <td>
                                            <strong>{{ rowLabel(group) }}</strong>
                                            <span v-if="rowWarning(group)" class="row-warning" :title="rowWarning(group)"> ⚠️</span>
                                        </td>

                                        <!-- Catégories (desktop uniquement) : une pastille par
                                        catégorie, cliquable pour pointer une seule catégorie. -->
                                        <td class="hideonmobile players-list-cell">
                                            <button v-for="c in rowCats(group)" class="checkin-cat" type="button"
                                                :class="{ checked: rowCatChecked(group, c), 'out-of-scope': !pointageScope.includes(c) }"
                                                :disabled="!canManage"
                                                :title="rowCatChecked(group, c) ? 'Pointé : cliquer pour dépointer' : 'Cliquer pour pointer cette catégorie seule'"
                                                @click="setRowCatCheckIn(group, c)" :key="c">
                                                {{ categoryShort(c) }}
                                            </button>
                                        </td>

                                        <td v-if="hasOnsitePayments" class="onsite-col">
                                            <button v-if="rowOnsiteDue(group) > 0" class="btn-collect" :disabled="!canManage || collecting"
                                                @click="setOnsitePaid(group, true)">
                                                💶 Encaisser {{ rowOnsiteDue(group) }} €
                                            </button>
                                            <button v-else-if="rowOnsitePaid(group) > 0" class="onsite-paid-badge"
                                                :disabled="!canManage || collecting" title="Cliquer pour annuler l'encaissement"
                                                @click="setOnsitePaid(group, false)">
                                                ✓ Payé sur place
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="mobile-cards">
                            <div v-for="group in filteredPlayerDebts" class="mobile-card"
                                :class="{ 'row-checkedin': rowStatus(group) === 'full' }"
                                :key="'card-' + group.paymentKey">
                                <div class="card-top">
                                    <button class="checkin-toggle" :disabled="!canManage"
                                        @click="canManage && toggleCheckIn(group)">
                                        <span class="card-name">
                                            <span class="checkin-indicator">{{ CHECKIN_ICONS[rowStatus(group)] }}</span>
                                            {{ rowLabel(group) }}
                                        </span>
                                        <span v-if="rowWarning(group)" class="row-warning" :title="rowWarning(group)">⚠️</span>
                                    </button>
                                </div>
                                <div class="card-info-row players-list-cell">
                                    <button v-for="c in rowCats(group)" class="checkin-cat" type="button"
                                        :class="{ checked: rowCatChecked(group, c), 'out-of-scope': !pointageScope.includes(c) }"
                                        :disabled="!canManage"
                                        @click="setRowCatCheckIn(group, c)" :key="c">
                                        {{ categoryShort(c) }}
                                    </button>
                                </div>
                                <div v-if="rowOnsiteDue(group) > 0 || rowOnsitePaid(group) > 0" class="card-info-row">
                                    <button v-if="rowOnsiteDue(group) > 0" class="btn-collect" :disabled="!canManage || collecting"
                                        @click="setOnsitePaid(group, true)">
                                        💶 Encaisser {{ rowOnsiteDue(group) }} €
                                    </button>
                                    <button v-else class="onsite-paid-badge" :disabled="!canManage || collecting"
                                        @click="setOnsitePaid(group, false)">
                                        ✓ Payé sur place
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
                <p v-else class="no-data">Aucun joueur à pointer pour l'instant.</p>

                <Teleport to="body">
                    <div v-if="showExportModal" class="modal-overlay">
                        <div class="modal-background" @click="showExportModal = false"></div>
                        <div class="modal-content export-modal-content">
                            <h3>{{ exportAll ? 'Exporter tous les inscrits' : 'Exporter les participants' }}</h3>

                            <p class="export-modal-label">Logiciel</p>
                            <div class="export-software-grid">
                                <button v-for="sw in EXPORT_SOFTWARES" class="export-software-card"
                                    :class="{ active: exportSoftware === sw.id }"
                                    @click="exportSoftware = sw.id" :key="sw.id">
                                    <img v-if="sw.logo" :src="sw.logo" :alt="sw.label" class="export-software-logo" />
                                    <span v-else class="export-software-badge">{{ sw.shortLabel }}</span>
                                    <span class="export-software-name">{{ sw.label }}</span>
                                </button>
                            </div>

                            <template v-if="exportNeedsCategory">
                                <p class="export-modal-label">Catégorie</p>
                                <div class="export-category-grid">
                                    <button v-for="cat in EXPORT_CATEGORIES" class="export-category-btn"
                                        :class="{ active: exportCategory === cat.id }"
                                        @click="exportCategory = cat.id" :key="cat.id">
                                        {{ exportAll ? cat.labelAll : cat.label }}
                                    </button>
                                </div>
                            </template>
                            <p v-else-if="isLinkedToBadarts" class="export-modal-hint">
                                Tous les inscrits Badarts seront exportés en un seul fichier.
                            </p>
                            <p v-else-if="exportAll" class="export-modal-hint">
                                Tous les inscrits, toutes catégories, seront exportés en un seul fichier, sans tenir
                                compte du pointage.
                            </p>
                            <p v-else class="export-modal-hint">
                                Les joueurs pointés dans les catégories sélectionnées (« Je pointe ») seront
                                exportés en un seul fichier.
                            </p>

                            <div class="form-actions">
                                <button class="btn-submit" @click="runExport">Exporter</button>
                                <button class="btn-secondary" @click="showExportModal = false">Annuler</button>
                            </div>
                        </div>
                    </div>
                </Teleport>
            </div>

            <!-- Onglet Paramètres : une section repliable par groupe de réglages, avec un
            résumé de la valeur actuelle dans l'en-tête pour ne pas avoir à tout déplier. -->
            <div v-show="activeTab === 'parametres'" class="settings-sections">
                <!-- Lien à partager sans repasser par le calendrier : la fiche publique
                (affiche, infos, bouton d'inscription, cf. EventDetails.vue). -->
                <details class="settings-section">
                    <summary class="settings-summary">
                        <span class="settings-title">Lien à partager</span>
                    </summary>
                    <div class="settings-body">
                        <div v-for="link in shareLinks" class="share-link-control" :key="link.key">
                            <label>{{ link.label }} :</label>
                            <input class="form-input share-link-input" readonly :value="link.url"
                                @focus="($event.target as HTMLInputElement).select()" />
                            <button class="btn-secondary" type="button" @click="copyShareLink(link)">
                                {{ linkCopied === link.key ? 'Copié !' : 'Copier le lien' }}
                            </button>
                            <a :href="link.url" target="_blank" rel="noopener noreferrer" class="share-link-open">
                                Ouvrir
                            </a>
                        </div>
                    </div>
                </details>

                <!-- Infos d'un tournoi local : nom, dates, lieu, affiche, lien externe... Seul
                endroit où elles s'éditent (plus de modale dans le calendrier ni sur CMER),
                accessible à l'organisateur/au créateur/à l'admin même sans premium -- ce
                n'est pas de la gestion d'inscriptions. Un officiel se modifie dans LSEF. -->
                <details v-if="canEditLocalInfo" class="settings-section">
                    <summary class="settings-summary">
                        <span class="settings-title">Infos du tournoi</span>
                    </summary>
                    <div class="settings-body">
                        <div class="form-row">
                            <div class="form-group">
                                <label>Nom du tournoi *</label>
                                <input v-model="infoForm.name" class="form-input" placeholder="Open de mon club" />
                            </div>
                            <div class="form-group">
                                <label>Club organisateur</label>
                                <select v-if="isSuperAdmin" v-model="infoForm.organizer_club_id" class="form-input"
                                    @change="onInfoOrganiserSelect">
                                    <option :value="null" disabled>Choisir un club...</option>
                                    <option v-for="club in clubOptions" :value="club.id" :key="club.id">{{ club.name }}</option>
                                </select>
                                <input v-else v-model="infoForm.club_name" class="form-input" readonly
                                    style="opacity: 0.7; cursor: default;" />
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group">
                                <label>Date de début *</label>
                                <input v-model="infoForm.event_date" type="date" class="form-input" />
                            </div>
                            <div class="form-group">
                                <label>Date de fin (optionnel)</label>
                                <input v-model="infoForm.event_date_end" type="date" class="form-input" />
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group">
                                <label>Lieu (ville, département...)</label>
                                <input v-model="infoForm.location" class="form-input" placeholder="Montpellier (34)" />
                            </div>
                            <div class="form-group">
                                <label>Format</label>
                                <select v-model="infoForm.format" class="form-input">
                                    <option value="simple">Simple</option>
                                    <option value="double">Double</option>
                                    <option value="simple+double">Simple + Double</option>
                                </select>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>Adresse exacte de la salle</label>
                            <input v-model="infoForm.address" class="form-input"
                                placeholder="12 rue des Lilas, 34000 Montpellier" />
                        </div>
                        <div class="form-row">
                            <div class="form-group">
                                <label>Lien externe (site, règlement, réservation...)</label>
                                <input v-model="infoForm.external_link" type="url" class="form-input" placeholder="https://..." />
                            </div>
                            <div class="form-group">
                                <label>Texte du bouton du lien</label>
                                <input v-model="infoForm.external_link_label" class="form-input" maxlength="40"
                                    placeholder="Plus d'infos" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label>Description</label>
                            <textarea v-model="infoForm.description" class="form-input" rows="4"
                                placeholder="Informations complémentaires..." />
                        </div>
                        <div class="form-group">
                            <label>Message après inscription (affiché au joueur une fois son paiement confirmé)</label>
                            <textarea v-model="infoForm.confirmation_message" class="form-input" rows="3"
                                placeholder="Ex. : Pointage de 9h à 9h45 à l'accueil, merci d'arriver en avance." />
                        </div>
                        <div class="form-group">
                            <label>Affiche{{ event.affiche ? ' (remplacer)' : '' }}</label>
                            <img v-if="afficheUrl" :src="afficheUrl" alt="Affiche actuelle" class="affiche-preview" />
                            <input type="file" class="form-input" accept="image/*" @change="onInfoAfficheChange" />
                        </div>
                        <div class="settings-actions">
                            <button class="btn-save-limits" :disabled="savingInfo" @click="saveLocalInfo">
                                {{ savingInfo ? '...' : 'Sauvegarder' }}
                            </button>
                        </div>
                    </div>
                </details>

                <!-- Présentation d'un tournoi officiel : l'entité gestionnaire (souvent le
                club hôte) en gère l'affiche, la description et le lien externe. Nom, dates
                et lieu restent fixés par le comité/la ligue dans le calendrier officiel (LSEF). -->
                <details v-if="canEditPresentation" class="settings-section">
                    <summary class="settings-summary">
                        <span class="settings-title">Présentation du tournoi</span>
                    </summary>
                    <div class="settings-body">
                        <p class="presentation-hint">
                            Le nom, les dates et le lieu sont fixés par le calendrier officiel : pour les modifier,
                            contactez votre comité.
                        </p>
                        <div class="form-group">
                            <label>Adresse exacte de la salle</label>
                            <input v-model="infoForm.address" class="form-input"
                                placeholder="12 rue des Lilas, 34000 Montpellier" />
                        </div>
                        <div class="form-row">
                            <div class="form-group">
                                <label>Lien externe (site, règlement, réservation...)</label>
                                <input v-model="infoForm.external_link" type="url" class="form-input" placeholder="https://..." />
                            </div>
                            <div class="form-group">
                                <label>Texte du bouton du lien</label>
                                <input v-model="infoForm.external_link_label" class="form-input" maxlength="40"
                                    placeholder="Plus d'infos" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label>Description</label>
                            <textarea v-model="infoForm.description" class="form-input" rows="4"
                                placeholder="Informations complémentaires..." />
                        </div>
                        <div class="form-group">
                            <label>Message après inscription (affiché au joueur une fois son paiement confirmé)</label>
                            <textarea v-model="infoForm.confirmation_message" class="form-input" rows="3"
                                placeholder="Ex. : Pointage de 9h à 9h45 à l'accueil, merci d'arriver en avance." />
                        </div>
                        <div class="form-group">
                            <label>Affiche{{ event.affiche ? ' (remplacer)' : '' }}</label>
                            <img v-if="afficheUrl" :src="afficheUrl" alt="Affiche actuelle" class="affiche-preview" />
                            <input type="file" class="form-input" accept="image/*" @change="onInfoAfficheChange" />
                        </div>
                        <div class="settings-actions">
                            <button class="btn-save-limits" :disabled="savingInfo" @click="savePresentation">
                                {{ savingInfo ? '...' : 'Sauvegarder' }}
                            </button>
                        </div>
                    </div>
                </details>

                <!-- Tarifs : un tournoi local les règle même sans premium (comme ses infos, via
                PATCH /events/local), un officiel via inscription-settings (premium). -->
                <details v-if="canEditPricing" class="settings-section">
                    <summary class="settings-summary">
                        <span class="settings-title">Tarifs</span>
                        <span class="settings-hint">{{ pricingSummary }}</span>
                    </summary>
                    <div class="settings-body">
                        <div v-if="hasDoubles" class="pricing-mode">
                            <span class="pricing-mode-label">Mode de tarification :</span>
                            <div class="status-buttons" role="group" aria-label="Mode de tarification">
                                <button type="button" class="status-btn" :class="{ active: !pricingForm.team_pricing }"
                                    :aria-pressed="!pricingForm.team_pricing" @click="pricingForm.team_pricing = false">
                                    Prix par joueur et par catégorie
                                </button>
                                <button type="button" class="status-btn" :class="{ active: pricingForm.team_pricing }"
                                    :aria-pressed="pricingForm.team_pricing" @click="pricingForm.team_pricing = true">
                                    Prix par équipe pour les doubles
                                </button>
                            </div>
                        </div>
                        <div class="pricing-row">
                            <span class="pricing-row-title">
                                {{ pricingForm.team_pricing ? 'Individuel, par joueur et par catégorie' : 'Par joueur et par catégorie' }}
                            </span>
                            <label>{{ singleRate ? 'Tarif' : 'Licencié' }}
                                <input v-model.number="pricingForm.licensed" type="number" min="0" step="0.5" class="pricing-input" /> €
                            </label>
                            <label v-if="!singleRate">Non licencié
                                <input v-model.number="pricingForm.unlicensed" type="number" min="0" step="0.5" class="pricing-input" /> €
                            </label>
                        </div>
                        <div v-if="pricingForm.team_pricing && hasDoubles" class="pricing-row">
                            <span class="pricing-row-title">Double, par équipe et par catégorie</span>
                            <label>{{ singleRate ? 'Tarif' : 'Équipe licenciée' }}
                                <input v-model.number="pricingForm.team_licensed" type="number" min="0" step="0.5" class="pricing-input" /> €
                            </label>
                            <label v-if="!singleRate">Équipe non licenciée
                                <input v-model.number="pricingForm.team_unlicensed" type="number" min="0" step="0.5" class="pricing-input" /> €
                            </label>
                        </div>
                        <p v-if="pricingForm.team_pricing && hasDoubles && !singleRate" class="helloasso-missing-hint">
                            Équipe mixte : chaque joueur paie la moitié du prix d'équipe correspondant à son statut.
                        </p>
                        <div class="settings-actions">
                            <button class="btn-save-limits" :disabled="savingPricing" @click="savePricing">
                                {{ savingPricing ? '...' : 'Sauvegarder' }}
                            </button>
                        </div>
                    </div>
                </details>

                <div v-if="isLinkedToBadarts" class="settings-banner">
                    <p class="info-banner">
                        Ce tournoi est géré sur Badarts (inscriptions, paiement, pointage). Cette page
                        n'affiche que les infos et la liste des participants en lecture seule (onglet
                        "Participants").
                    </p>
                </div>
                <div v-else-if="!canManage" class="settings-banner">
                    <div class="premium-banner">
                        <div class="premium-banner-icon">🔒</div>
                        <div class="premium-banner-body">
                            <p class="premium-banner-title">Gestion des inscriptions : accès premium</p>
                            <p class="premium-banner-text">
                                Paiement en ligne ou sur place, pointage, catégories ouvertes et limites par catégorie sont
                                réservés aux tournois avec un accès premium actif : à l'unité pour cet événement, ou
                                à la saison pour tous vos événements.
                            </p>
                            <div class="premium-banner-actions">
                                <router-link to="/services#tarifs" class="premium-banner-cta">Voir les tarifs</router-link>
                                <a :href="`${authUrl}/contact`" target="_blank" rel="noopener noreferrer" class="premium-banner-cta">
                                    Nous contacter
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <template v-if="canManage">
                    <details class="settings-section">
                        <summary class="settings-summary">
                            <span class="settings-title">Inscriptions</span>
                            <span class="settings-hint">{{ registrationSummary }}</span>
                        </summary>
                        <div class="settings-body">
                            <div class="registration-status-control">
                                <label>Statut :</label>
                                <div class="status-buttons">
                                    <button v-for="opt in visibleStatusOptions" class="status-btn"
                                        :class="{ active: (event.registration_status ?? 'open') === opt.value }"
                                        :disabled="togglingRegistrations || (opt.requiresPaymentMode && !event.registration_open)"
                                        :title="opt.requiresPaymentMode && !event.registration_open
                                            ? 'Aucun mode de paiement disponible : choisissez-en un dans la section Paiement avant de pouvoir ouvrir les inscriptions.'
                                            : ''"
                                        @click="setRegistrationStatus(opt.value)" :key="opt.value">
                                        {{ opt.icon }} {{ opt.label }}
                                    </button>
                                </div>
                                <p v-if="!event.registration_open" class="helloasso-missing-hint">
                                    ⚠️ Aucun mode de paiement disponible : acceptez le paiement sur place, ou
                                    <router-link :to="ownerSettings.route">configurez HelloAsso dans les paramètres de {{ ownerSettings.label }}</router-link>
                                    pour le paiement en ligne.
                                </p>
                            </div>

                            <!-- Butoir de clôture automatique : au-delà, le serveur refuse toute inscription
                            publique sans toucher au statut ci-dessus (cf. events/routes.py::registration_deadline_passed). -->
                            <div class="registration-status-control deadline-control">
                                <label for="registration-deadline">Clôture automatique :</label>
                                <input id="registration-deadline" v-model="deadlineForm" type="datetime-local" class="deadline-input"
                                    :disabled="savingDeadline" />
                                <button class="btn-secondary" :disabled="savingDeadline || deadlineForm === deadlineInitial"
                                    @click="saveDeadline">
                                    {{ savingDeadline ? '...' : 'Enregistrer' }}
                                </button>
                                <button v-if="event.registration_deadline" class="btn-secondary" :disabled="savingDeadline"
                                    @click="clearDeadline">
                                    Retirer
                                </button>
                                <p v-if="deadlinePassed(event)" class="helloasso-missing-hint deadline-passed">
                                    🔒 Inscriptions clôturées automatiquement depuis le {{ formatDeadline(event.registration_deadline) }}.
                                    Reculez la date ou retirez-la pour rouvrir.
                                </p>
                                <p v-else-if="event.registration_deadline" class="helloasso-missing-hint">
                                    ⏳ Les inscriptions se fermeront automatiquement le {{ formatDeadline(event.registration_deadline) }}.
                                </p>
                                <p v-else class="helloasso-missing-hint">
                                    Aucune date limite : les inscriptions restent ouvertes tant que le statut ci-dessus n'est pas "Fermé".
                                </p>
                            </div>
                        </div>
                    </details>

                    <!-- Modes de paiement acceptés (cf. Event.accept_online_payment/accept_onsite_payment
                    côté back) : en ligne n'est réellement proposé que si HelloAsso est configuré. -->
                    <details class="settings-section">
                        <summary class="settings-summary">
                            <span class="settings-title">Paiement</span>
                            <span class="settings-hint">{{ paymentSummary }}</span>
                        </summary>
                        <div class="settings-body">
                            <div class="registration-status-control payment-modes-control">
                                <label>Paiement accepté :</label>
                                <label class="checkbox-label"
                                    :title="event.helloasso_configured ? '' : `HelloAsso non configuré pour ${ownerSettings.label}`">
                                    <input type="checkbox" :checked="event.accept_online_payment && event.helloasso_configured"
                                        :disabled="savingPaymentModes || !event.helloasso_configured"
                                        @change="setPaymentMode('accept_online_payment', $event)" />
                                    En ligne (HelloAsso)
                                </label>
                                <label class="checkbox-label">
                                    <input type="checkbox" :checked="event.accept_onsite_payment" :disabled="savingPaymentModes"
                                        @change="setPaymentMode('accept_onsite_payment', $event)" />
                                    Sur place, le jour du tournoi
                                </label>
                                <p v-if="!event.helloasso_configured" class="helloasso-missing-hint">
                                    Paiement en ligne indisponible : HelloAsso n'est pas configuré pour {{ ownerSettings.label }}
                                    (<router-link :to="ownerSettings.route">paramètres</router-link>).
                                </p>
                                <p v-if="event.accept_onsite_payment" class="helloasso-missing-hint">
                                    Les inscriptions à régler sur place réservent leur place tout de suite ; vous les encaissez
                                    depuis l'onglet Pointage.
                                </p>
                            </div>
                        </div>
                    </details>

                    <!-- Catégories ouvertes : seul un tournoi local peut restreindre -- un
                    officiel reste toujours ouvert à toutes par défaut (event.open_categories
                    y vaut toujours null). -->
                    <details v-if="isLocal" class="settings-section">
                        <summary class="settings-summary">
                            <span class="settings-title">Catégories ouvertes</span>
                            <span class="settings-hint">{{ openCategoriesSummary }}</span>
                        </summary>
                        <div class="settings-body">
                            <div class="open-categories-grid">
                                <label v-for="cat in ALL_CATEGORIES" class="open-cat-item" :key="cat.value">
                                    <input v-model="openCategories[cat.value]" type="checkbox" />
                                    {{ cat.short }}
                                </label>
                            </div>
                            <div class="settings-actions">
                                <button class="btn-save-limits" :disabled="savingOpenCategories" @click="saveOpenCategories">
                                    {{ savingOpenCategories ? '...' : 'Sauvegarder' }}
                                </button>
                            </div>
                        </div>
                    </details>

                    <details class="settings-section">
                        <summary class="settings-summary">
                            <span class="settings-title">Limites par catégorie</span>
                            <span class="settings-hint">{{ limitsSummary }}</span>
                        </summary>
                        <div class="settings-body">
                            <div class="limits-grid">
                                <template v-for="cat in ALL_CATEGORIES" :key="cat.value">
                                    <div class="limit-row">
                                        <label class="limit-label">{{ cat.short }}</label>
                                        <span class="limit-count">{{ getCategoryCount(cat) }}</span>
                                        <span class="limit-sep">/</span>
                                        <input v-model.number="categoryLimits[cat.value]" type="number"
                                            min="0" placeholder="-" class="limit-input" />
                                    </div>
                                </template>
                            </div>
                            <div class="settings-actions">
                                <button class="btn-save-limits" :disabled="savingLimits" @click="saveCategoryLimits">
                                    {{ savingLimits ? '...' : 'Sauvegarder' }}
                                </button>
                            </div>
                        </div>
                    </details>
                </template>
            </div>
        </div>
    </div>

        <div v-else class="centered-block">
            <p v-if="event?.can_manage">
                Cet événement nécessite un accès premium actif pour être géré. Contactez-nous pour l'activer.
            </p>
            <p v-else>Vous n'avez pas les droits suffisants pour accéder à cette section.</p>
            <router-link to="/" class="cta-link">← Retour à l'accueil</router-link>
        </div>
    </div>

    <div v-else class="centered-block">
        <h2>Connexion requise</h2>
        <p>Veuillez vous connecter pour accéder à cette section.</p>
        <button @click="authStore.openAuthModal()">Se connecter</button>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import backendApi from '@/axios/backendApi'
import { toast } from 'vue3-toastify'
import { useAuthStore } from '@/stores/useAuthStore'
import { calculateFfdCategory, eligibleSimpleCategories, existingCategoriesForPlayer, handleError, formatEventDateRange, formatFirstname, ineligibleDoubleCategories, normalizeFirstname, comparePlayerNames, orderPair, wouldViolateSimpleCumul } from '@/functions/utils'
import { deadlinePassed, doubleShareCents, eventPath, formatDeadline, formatPrice, parseEventId, teamPrices } from '@/functions/eventDisplay'
import { getCurrentSeason, app_url, auth_url as authUrl, backend_url } from '@/config/config'
import { useClubsStore } from '@/stores/useClubsStore';
const clubsStore = useClubsStore();
clubsStore.load();
import CategorySelector from '@/components/CategorySelector.vue'
import PlayerPicker, { type RosterEntry } from '@/components/PlayerPicker.vue'
import ConfirmModal from '@/components/ConfirmModal.vue';
import HelloAssoImportModal from '@/components/HelloAssoImportModal.vue'
import { useConfirm } from '@/composables/useConfirm';
import darts4windowsLogo from '@/assets/darts4windows-logo.jpg'
import threeKDartsLogo from '@/assets/3kdarts-logo.png'
import dartConnectLogo from '@/assets/dartconnect-logo.png'
import dartUpLogo from '@/assets/dartup-logo.png'

const route = useRoute()
const authStore = useAuthStore()

const { confirm } = useConfirm();

const eventId = computed(() => parseEventId(route.params.eventId))

const SIMPLE_CATEGORIES = [
    { value: 'M', label: 'Individuel Masculin', short: 'Masculin', isDouble: false },
    { value: 'V', label: 'Individuel Vétéran', short: 'Vétéran', isDouble: false },
    { value: 'F', label: 'Individuel Féminin', short: 'Féminin', isDouble: false },
    { value: 'J', label: 'Individuel Junior', short: 'Junior', isDouble: false },
]

const DOUBLE_CATEGORIES = [
    { value: 'DM', label: 'Double', short: 'Double', isDouble: true },
    { value: 'DF', label: 'Double Féminin', short: 'Dbl Féminin', isDouble: true },
]

const ALL_CATEGORIES = [...SIMPLE_CATEGORIES, ...DOUBLE_CATEGORIES]

// Onglet Inscriptions : une catégorie fermée (organisateur d'un tournoi local, cf.
// openCategories) et sans aucune inscription existante n'a rien à montrer -- on ne
// l'affiche que si elle est ouverte OU si elle porte déjà au moins une inscription
// (ex. inscrite avant sa fermeture). Sans effet pour un tournoi officiel : openCategories
// y reste toujours "tout ouvert" (event.open_categories vaut toujours null). Les
// réglages ("Catégories ouvertes"/"Limites par catégorie") continuent, eux, d'utiliser
// ALL_CATEGORIES : l'organisateur doit pouvoir rouvrir une catégorie masquée ici.
const visibleCategories = computed(() =>
    ALL_CATEGORIES.filter(cat => (openCategories.value[cat.value] ?? true) || getByCategory(cat).length > 0)
)
// Formulaire d'ajout manuel : seules les catégories ouvertes sont proposées, comme sur
// le formulaire public (cf. EventRegistrationForm.vue::availableSimpleCategories) -- le
// backend refuserait de toute façon une catégorie fermée (_validate_category_eligibility).
const openSimpleCategories = computed(() => SIMPLE_CATEGORIES.filter(cat => openCategories.value[cat.value] ?? true))
const openDoubleCategories = computed(() => DOUBLE_CATEGORIES.filter(cat => openCategories.value[cat.value] ?? true))

const TABS = [
    { id: 'inscriptions', label: 'Inscriptions' },
    { id: 'pointage', label: 'Pointage' },
    { id: 'roster', label: 'Participants' },
    { id: 'parametres', label: 'Paramètres' },
] as const
type TabId = typeof TABS[number]['id']
const isTabId = (v: unknown): v is TabId => TABS.some(t => t.id === v)
// `?tab=parametres` : lien direct vers un onglet (ex. bouton "Gérer mon tournoi" de la
// fiche événement ou de CMER, qui mène aux infos du tournoi).
const activeTab = ref<TabId>(isTabId(route.query.tab) ? route.query.tab : 'inscriptions')
// Event lié à un tournoi Badarts : inscriptions/pointage vivent sur Badarts, seuls
// Participants (lecture seule) et Paramètres ont un sens ici. Sinon, sans premium, seul
// l'onglet Paramètres (infos du tournoi, lien public) est accessible -- les
// inscriptions/le pointage sont gatés côté serveur (402).
const visibleTabs = computed(() => {
    if (isLinkedToBadarts.value) return TABS.filter(t => t.id === 'roster' || t.id === 'parametres')
    return canManage.value ? TABS.filter(t => t.id !== 'roster') : TABS.filter(t => t.id === 'parametres')
})

// Lien à partager, copiable depuis l'onglet Paramètres (cf. template) : la fiche publique
// seule, qui réunit toutes les infos et les boutons (dont "S'inscrire") -- un lien direct
// vers le formulaire d'inscription ferait sauter ces infos.
type ShareLink = { key: string; label: string; url: string }
const shareLinks = computed<ShareLink[]>(() => {
    const ev = { id: eventId.value, name: event.value?.name ?? '', display_name: event.value?.display_name, place: event.value?.location }
    return [
        { key: 'fiche', label: 'Lien de la fiche', url: `${app_url}${eventPath(ev)}` },
    ]
})
const linkCopied = ref<string | null>(null)
const copyShareLink = async (link: ShareLink) => {
    try {
        await navigator.clipboard.writeText(link.url)
        linkCopied.value = link.key
        setTimeout(() => { linkCopied.value = null }, 1500)
    } catch {
        // Presse-papier indisponible : l'utilisateur peut toujours sélectionner le lien à la main.
        toast.info(link.url)
    }
}

interface Inscription {
    id: number
    event_id: number
    name: string
    firstname: string
    club: string
    player_number: number | null
    category_simple: string | null
    category_double: string | null
    doublette: number | null
    registered_by_user_id: number | null
    checked_in: boolean
    checked_in_categories: string | null
    // Statut licencié retenu à l'inscription et code affiché entre parenthèses selon le
    // niveau de l'événement (club au comité, comité en ligue/fédéral, "NL") -- calculés
    // côté serveur, cf. Dartshub/backend/modules/api/inscriptions/club_display.py.
    is_licensed?: boolean
    display_code?: string | null
}

interface DebtGroup {
    registrantUserId: number | null
    paidByUserId: number | null
    paymentKey: string
    registrantName: string
    registrantFirstname: string
    players: Array<{ name: string; firstname: string; categories: string; amount: number }>
    total: number
    paymentAmount: number | null
    isPaid: boolean
    paidByName?: string | null
    // Montants (€) encore à encaisser / déjà encaissés sur place (paiements "onsite").
    onsiteDue: number
    onsitePaid: number
}

interface InscriptionPaymentInfo {
    inscription_id: number
    is_paid: boolean
    amount_paid_cents: number
    amount_due_onsite_cents: number
    amount_paid_onsite_cents: number
    paid_by_user_id: number | null
    paid_by_name: string | null
    payment_method: string | null
}

const event = ref<any>(null)
// Droit d'AGIR (check-in, ajout/suppression d'inscription, import...) : reflète l'accès
// réel calculé par le serveur pour CET événement précis -- propriétaire désigné (club
// organisateur, ou comité/ligue quand ils gèrent directement) ET premium actif, ou
// super_admin. Cf. Dartshub/backend/modules/api/clubs/premium.py::require_event_manager
// et events/routes.py::_to_event_response (can_manage_now). Avant, ce filtre se basait
// sur le rôle global (club_representative) sans vérifier ni le bon club ni le premium :
// les boutons de gestion s'affichaient même sans premium, pour échouer côté serveur
// (402) au clic -- on ne gère QUE si le premium est actif, la page ne doit même pas
// prétendre l'inverse.
// NULL = event natif DartsHub. Sinon, inscriptions/pointage/tarifs restent gérés côté
// Badarts -- can_manage_now peut malgré tout valoir vrai ici (le club organisateur a
// peut-être aussi le premium DartsHub actif pour SES AUTRES tournois), donc on l'exclut
// explicitement plutôt que de laisser transparaître les réglages natifs (pointage, tarifs,
// catégories) sur un tournoi dont les vraies inscriptions vivent ailleurs. Cf.
// Dartshub/backend/modules/api/events/routes.py::get_badarts_roster.
const isLinkedToBadarts = computed(() => !!event.value?.linked_badarts_tournament_id)
const canManage = computed(() => !isLinkedToBadarts.value && !!event.value?.can_manage_now)
// Un tournoi local n'a ni tarif licencié/non licencié distinct, ni statut "Licenciés
// uniquement" (aucune licence vérifiée) -- et son organisateur choisit explicitement
// quelles catégories sont ouvertes, plutôt que toutes par défaut. Cf. le même
// branchement sur event.event_type côté backend (events/routes.py).
const isLocal = computed(() => event.value?.event_type === 'local')
const isSuperAdmin = computed(() => authStore.scopes.includes('super_admin'))
// Droit de modifier les INFOS d'un tournoi local (nom, dates, lieu, affiche...), sans
// rapport avec le premium : miroir de events/routes.py::_require_local_event_manager
// (super_admin, créateur, ou représentant du club organisateur -- ce dernier via
// can_manage, cf. clubs/premium.py::can_manage_event). Le rôle comité n'y donne aucun
// droit (règle du 2026-09-24) : seul le super_admin peut aussi changer le club organisateur.
const canEditLocalInfo = computed(() => isLocal.value && !!event.value && (
    !!event.value.can_manage || isSuperAdmin.value || event.value.created_by_email === authStore.email
))

// Présentation d'un tournoi OFFICIEL (affiche, description, lien externe) : confiée à
// l'entité gestionnaire, miroir de events/routes.py::update_event_inscription_settings
// et upload_affiche (require_event_manager, donc premium compris) -- le reste des infos
// d'un officiel (nom, dates, lieu) se modifie dans le calendrier LSEF.
const canEditPresentation = computed(() => !isLocal.value && canManage.value)
// URL absolue (event lié Badarts, cf. isLinkedToBadarts) = affiche sur l'autre service,
// jamais préfixée par backend_url (celui de DartsHub) sous peine de 404.
const afficheUrl = computed(() => {
    const a = event.value?.affiche
    if (!a) return null
    return /^https?:\/\//.test(a) ? a : `${backend_url}${a}`
})

const savingInfo = ref(false)
const infoAffiche = ref<File | null>(null)
const clubOptions = ref<{ id: number; name: string }[]>([])
const infoForm = ref({
    name: '', club_name: '', organizer_club_id: null as number | null, location: '', address: '', event_date: '',
    event_date_end: '', format: 'simple+double', external_link: '',
    external_link_label: '', description: '', confirmation_message: '',
})

const fillInfoForm = () => {
    const e = event.value
    if (!e) return
    infoForm.value = {
        name: e.name ?? '',
        club_name: e.club_name ?? '',
        organizer_club_id: e.organizer_club_id ?? null,
        location: e.location ?? '',
        address: e.address ?? '',
        event_date: e.event_date ?? '',
        event_date_end: e.event_date_end ?? '',
        format: e.format ?? 'simple+double',
        external_link: e.external_link ?? '',
        external_link_label: e.external_link_label ?? '',
        description: e.description ?? '',
        confirmation_message: e.confirmation_message ?? '',
    }
    infoAffiche.value = null
}

const ensureClubOptionsLoaded = async () => {
    if (!isSuperAdmin.value || clubOptions.value.length) return
    try {
        const { data } = await backendApi.get('/clubs/', { headers: { Authorization: `Bearer ${authStore.token}` } })
        clubOptions.value = data.map((c: any) => ({ id: c.id, name: c.name }))
    } catch { /* liste vide -- le club reste celui déjà enregistré */ }
}

const onInfoOrganiserSelect = () => {
    infoForm.value.club_name = clubOptions.value.find(c => c.id === infoForm.value.organizer_club_id)?.name || ''
}

const onInfoAfficheChange = (e: Event) => {
    infoAffiche.value = (e.target as HTMLInputElement).files?.[0] ?? null
}

const saveLocalInfo = async () => {
    const f = infoForm.value
    if (!f.name.trim() || !f.event_date) {
        toast.error('Nom et date de début requis.')
        return
    }
    savingInfo.value = true
    try {
        const payload: Record<string, unknown> = {
            name: f.name.trim(),
            club_name: f.club_name || null,
            organizer_club_id: f.organizer_club_id,
            location: f.location || null,
            address: f.address.trim() || null,
            event_date: f.event_date,
            event_date_end: f.event_date_end || null,
            format: f.format || null,
            external_link: f.external_link || null,
            external_link_label: f.external_link_label.trim() || null,
            description: f.description || null,
            confirmation_message: f.confirmation_message.trim() || null,
        }
        await backendApi.patch(`/events/local/${eventId.value}`, payload, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        if (infoAffiche.value) {
            const formData = new FormData()
            formData.append('file', infoAffiche.value)
            await backendApi.post(`/events/local/${eventId.value}/upload-affiche`, formData, {
                headers: { Authorization: `Bearer ${authStore.token}` },
            })
        }
        await fetchEvent()
        toast.success('Infos du tournoi mises à jour.')
    } catch (err) {
        handleError(err, 'saving local event info')
    } finally {
        savingInfo.value = false
    }
}
const savePresentation = async () => {
    const f = infoForm.value
    savingInfo.value = true
    try {
        await backendApi.patch(`/events/${eventId.value}/inscription-settings`, {
            description: f.description || null,
            address: f.address.trim() || null,
            external_link: f.external_link || null,
            external_link_label: f.external_link_label.trim() || null,
            confirmation_message: f.confirmation_message.trim() || null,
        }, { headers: { Authorization: `Bearer ${authStore.token}` } })
        if (infoAffiche.value) {
            const formData = new FormData()
            formData.append('file', infoAffiche.value)
            await backendApi.post(`/events/${eventId.value}/upload-affiche`, formData, {
                headers: { Authorization: `Bearer ${authStore.token}` },
            })
        }
        await fetchEvent()
        toast.success('Présentation du tournoi mise à jour.')
    } catch (err) {
        handleError(err, 'saving event presentation')
    } finally {
        savingInfo.value = false
    }
}
// HelloAsso se configure chez l'entité désignée gestionnaire de CET événement
// (event.premium_owner_type), pas toujours le club organisateur -- un comité ou une
// ligue peut gérer directement (cf. Dartshub/backend/modules/api/clubs/premium.py::
// _is_owner_rep). Le lien/texte doivent pointer vers les bons paramètres. L'id de
// l'entité est passé en query (?club=/?comite=/?ligue=) : indispensable au super_admin,
// représentant de rien, pour que la page de paramètres sache QUELLE entité ouvrir ;
// ignoré côté backend pour un représentant (cf. clubs/routes.py::get_my_club).
const OWNER_SETTINGS: Record<string, { label: string; route: string; param: string }> = {
  club: { label: 'ce club', route: '/mon-club', param: 'club' },
  comite: { label: 'ce comité', route: '/mon-comite', param: 'comite' },
  ligue: { label: 'cette ligue', route: '/ma-ligue', param: 'ligue' },
}
const ownerSettings = computed(() => {
  const base = OWNER_SETTINGS[event.value?.premium_owner_type] ?? OWNER_SETTINGS.club
  const ownerId = event.value?.premium_owner_id ?? (base === OWNER_SETTINGS.club ? event.value?.organizer_club_id : null)
  return { label: base.label, route: ownerId ? `${base.route}?${base.param}=${ownerId}` : base.route }
})
const loading = ref(false)
const error = ref('')
const inscriptions = ref<Inscription[]>([])
const inscriptionPayments = ref<InscriptionPaymentInfo[]>([])
const search = ref('')
const globalSearch = ref('')
const categoryCollapsed = ref<Record<string, boolean>>(
    Object.fromEntries(ALL_CATEGORIES.map(cat => [cat.value, true]))
)
// Une recherche globale active déplie automatiquement les catégories qui ont des
// résultats, sans quoi il faudrait ouvrir chaque catégorie à la main pour voir si le
// joueur cherché s'y trouve (cf. demande : un seul champ, pas un par catégorie).
const isCategoryOpen = (cat: typeof ALL_CATEGORIES[number]) =>
    globalSearch.value.trim() ? getFilteredByCategory(cat).length > 0 : !categoryCollapsed.value[cat.value]

const normalize = (s: string) =>
    s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
const showAddForm = ref(false)
const importSource = ref<'helloasso' | 'ffd' | null>(null)
const onImported = async () => {
    importSource.value = null
    await Promise.all([fetchInscriptions(), fetchAdminInscriptionPayments()])
}
const submitting = ref(false)
const togglingRegistrations = ref(false)
const savingLimits = ref(false)
const categoryLimits = ref<Record<string, number | null>>({})
const savingOpenCategories = ref(false)
const openCategories = ref<Record<string, boolean>>({})

interface AdminPlayerEntry {
    name: string
    firstname: string
    is_licensed: boolean
    // Catégorie FFD (M/F/JG/JF/V) trouvée pour ce nom -- fait foi sur birth_date/gender
    // ci-dessous tant qu'elle est renseignée (licence réelle trouvée, cf.
    // checkPlayerLicense/pickPlayerLicense). Sert uniquement à griser les catégories
    // individuelles non éligibles côté client, la vérité reste
    // events/routes.py::_validate_category_eligibility.
    licenseCategory: string | null
    birth_date: string | null
    gender: string | null
    category_simple: string | null
    category_double: string | null
    partner_name: string
    partner_firstname: string
    partner_is_licensed: boolean
    // Nécessaire pour vérifier l'éligibilité à la Double Féminine (réservée aux
    // femmes, joueur ET partenaire) -- pré-rempli et verrouillé dès qu'une licence est
    // trouvée pour le/la partenaire.
    partner_gender: string | null
    partner_name_2: string
    partner_firstname_2: string
    partner_is_licensed_2: boolean
}

const createEmptyAdminPlayer = (): AdminPlayerEntry => ({
    name: '',
    firstname: '',
    is_licensed: false,
    licenseCategory: null,
    birth_date: null,
    gender: null,
    category_simple: null,
    category_double: null,
    partner_name: '',
    partner_firstname: '',
    partner_is_licensed: false,
    partner_gender: null,
    partner_name_2: '',
    partner_firstname_2: '',
    partner_is_licensed_2: false,
})

const adminPlayers = ref<AdminPlayerEntry[]>([createEmptyAdminPlayer()])

const licenseCheckTimers = new Map<string, ReturnType<typeof setTimeout>>()

interface LicenseCheckResult {
    is_licensed: boolean
    category: string | null
    gender: string | null
    birth_date: string | null
}

const scheduleLicenseCheck = (
    key: string,
    name: string,
    firstname: string,
    apply: (result: LicenseCheckResult) => void,
) => {
    const existing = licenseCheckTimers.get(key)
    if (existing) clearTimeout(existing)
    licenseCheckTimers.set(key, setTimeout(async () => {
        if (!name.trim() || !firstname.trim()) {
            apply({ is_licensed: false, category: null, gender: null, birth_date: null })
            return
        }
        try {
            const { data } = await backendApi.get(`/events/${eventId.value}/check-license`, {
                params: { name, firstname },
                headers: { Authorization: `Bearer ${authStore.token}` },
            })
            apply({
                is_licensed: !!data?.is_licensed,
                category: data?.category ?? null,
                gender: data?.gender ?? null,
                birth_date: data?.birth_date ?? null,
            })
        } catch {
            // échec réseau : la case reste telle quelle, modifiable à la main
        }
    }, 400))
}

const checkPlayerLicense = (player: AdminPlayerEntry, index: number) =>
    scheduleLicenseCheck(`player-${index}`, player.name, player.firstname, (r) => {
        player.is_licensed = r.is_licensed
        player.licenseCategory = r.category
        if (r.is_licensed) {
            player.birth_date = r.birth_date
            player.gender = r.gender
        }
    })

const checkPartnerLicense = (player: AdminPlayerEntry, index: number) =>
    scheduleLicenseCheck(`partner-${index}`, player.partner_name, player.partner_firstname, (r) => {
        player.partner_is_licensed = r.is_licensed
        if (r.is_licensed) player.partner_gender = r.gender
    })

const checkPartner2License = (player: AdminPlayerEntry, index: number) =>
    scheduleLicenseCheck(`partner2-${index}`, player.partner_name_2, player.partner_firstname_2, (r) => { player.partner_is_licensed_2 = r.is_licensed })

const playerSuggestions = ref<Record<number, RosterEntry[]>>({})
const partnerSuggestions = ref<Record<number, RosterEntry[]>>({})
const partner2Suggestions = ref<Record<number, RosterEntry[]>>({})

const searchLicenses = async (query: string): Promise<RosterEntry[]> => {
    if (query.trim().length < 2) return []
    try {
        const { data } = await backendApi.get(`/events/${eventId.value}/search-licenses`, {
            params: { q: query },
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        return data ?? []
    } catch {
        return []
    }
}

const onPlayerSearch = async (index: number, query: string) => {
    playerSuggestions.value[index] = await searchLicenses(query)
}
const onPartnerSearch = async (index: number, query: string) => {
    partnerSuggestions.value[index] = await searchLicenses(query)
}
const onPartner2Search = async (index: number, query: string) => {
    partner2Suggestions.value[index] = await searchLicenses(query)
}

const pickPlayerLicense = (player: AdminPlayerEntry, entry: RosterEntry) => {
    player.name = entry.name
    player.firstname = entry.firstname
    player.is_licensed = true
    player.licenseCategory = entry.category ?? null
    player.birth_date = entry.birth_date ? entry.birth_date.slice(0, 10) : null
    player.gender = entry.gender ?? null
}
const pickPartnerLicense = (player: AdminPlayerEntry, entry: RosterEntry) => {
    player.partner_name = entry.name
    player.partner_firstname = entry.firstname
    player.partner_is_licensed = true
    player.partner_gender = entry.gender ?? null
}
const pickPartner2License = (player: AdminPlayerEntry, entry: RosterEntry) => {
    player.partner_name_2 = entry.name
    player.partner_firstname_2 = entry.firstname
    player.partner_is_licensed_2 = true
}

const adminHasDoubleDoublette = (player: AdminPlayerEntry): boolean =>
    (player.category_double || '').split(',').filter(c => c.trim()).length >= 2

const adminGetFirstDoubleCat = (player: AdminPlayerEntry): string =>
    (player.category_double || '').split(',').map(c => c.trim()).filter(Boolean)[0] ?? ''

const adminGetSecondDoubleCat = (player: AdminPlayerEntry): string =>
    (player.category_double || '').split(',').map(c => c.trim()).filter(Boolean)[1] ?? ''


// Catégories individuelles grisées pour ce joueur : non éligibles (Vétéran/Féminin/
// Junior selon sa catégorie FFD réelle et son genre -- licence trouvée ci-dessus sinon
// genre/date de naissance déclarés), qui romprait la règle de cumul (une seule
// catégorie, sauf exception junior : Mixte pour un garçon, Féminine pour une fille), ou
// déjà portée par une ligne d'inscription existante pour cet événement
// (inscriptions.value, déjà chargé pour cette page de gestion) -- la reproposer n'aurait
// aucun effet. Jamais affiché "Complet" (pas de quota ici) : cf.
// CategorySelector::ineligibleReasons. Renvoie la raison affichée sur chaque catégorie
// bloquée. Miroir de events/routes.py::_validate_category_eligibility, qui reste la
// vérité (même logique que EventRegistrationForm.vue::blockedSimpleCategories).
const REASON_ALREADY = 'Déjà inscrit'
const REASON_PARTNER_ALREADY = 'Partenaire déjà inscrit'
const REASON_INELIGIBLE = 'Non éligible'
const REASON_CUMUL = 'Non cumulable'

const playerFfdCategory = (player: AdminPlayerEntry): string | null => player.licenseCategory
    ? player.licenseCategory
    : calculateFfdCategory(player.gender, player.birth_date, getCurrentSeason())

const blockedSimpleCategories = (player: AdminPlayerEntry): Record<string, string> => {
    const existing = [...existingCategoriesForPlayer(inscriptions.value, player.name, player.firstname).simple]
    const blocked: Record<string, string> = {}
    for (const cat of existing) blocked[cat] = REASON_ALREADY
    if (isLocal.value) {
        // Pas de vérification d'âge/genre/licence pour un tournoi local -- seule règle
        // restante : une catégorie individuelle à la fois, comme sur le formulaire public
        // (EventRegistrationForm.vue) et le backend.
        for (const cat of SIMPLE_CATEGORIES) {
            if (!blocked[cat.value] && existing.length > 0) blocked[cat.value] = REASON_CUMUL
        }
        return blocked
    }
    const ffdCategory = playerFfdCategory(player)
    const eligible = eligibleSimpleCategories(ffdCategory, player.gender)
    // Cumul évalué avec les seules catégories déjà enregistrées pour ce joueur ; un
    // conflit avec une catégorie juste cochée ne grise rien : la cliquer la remplace
    // (cf. simpleCumulable).
    for (const cat of SIMPLE_CATEGORIES) {
        if (blocked[cat.value]) continue
        if (!eligible.has(cat.value)) {
            blocked[cat.value] = REASON_INELIGIBLE
        } else if (wouldViolateSimpleCumul(existing, cat.value, ffdCategory)) {
            blocked[cat.value] = REASON_CUMUL
        }
    }
    return blocked
}

// Cumul entre catégories cochées (cf. CategorySelector::isCumulable) : même règle que
// blockedSimpleCategories, une catégorie non cumulable remplace les autres.
const simpleCumulable = (player: AdminPlayerEntry) => (current: string[], candidate: string): boolean =>
    isLocal.value ? current.length === 0 : !wouldViolateSimpleCumul(current, candidate, playerFfdCategory(player))

// Catégorie double bloquée : non éligible (Double Féminine réservée aux femmes) ou déjà
// portée par le joueur OU le/la partenaire.
const blockedDoubleCategories = (player: AdminPlayerEntry): Record<string, string> => {
    const blocked: Record<string, string> = {}
    for (const cat of existingCategoriesForPlayer(inscriptions.value, player.name, player.firstname).double) {
        blocked[cat] = REASON_ALREADY
    }
    for (const cat of existingCategoriesForPlayer(inscriptions.value, player.partner_name, player.partner_firstname).double) {
        blocked[cat] ??= REASON_PARTNER_ALREADY
    }
    if (!isLocal.value) {
        for (const cat of ineligibleDoubleCategories(player.gender, player.partner_gender)) blocked[cat] ??= REASON_INELIGIBLE
    }
    return blocked
}

// Catégorie cochée mais bloquée (ex. joueur déjà inscrit saisi après avoir coché) :
// l'ajout est refusé tant qu'elle n'est pas décochée.
const blockedSelectionError = (player: AdminPlayerEntry): string | null => {
    const checks: [string | null, Record<string, string>, { value: string, label: string }[]][] = [
        [player.category_simple, blockedSimpleCategories(player), SIMPLE_CATEGORIES],
        [player.category_double, blockedDoubleCategories(player), DOUBLE_CATEGORIES],
    ]
    for (const [selected, blocked, categories] of checks) {
        for (const cat of (selected ?? '').split(',').map(c => c.trim()).filter(Boolean)) {
            if (blocked[cat]) {
                const label = categories.find(c => c.value === cat)?.label ?? cat
                return `${label} : ${blocked[cat].toLowerCase()}`
            }
        }
    }
    return null
}

const getAdminPlayerPrice = (player: AdminPlayerEntry): number => {
    const rate = player.is_licensed ? licensedRate.value : unlicensedRate.value
    let total = 0
    if (player.category_simple) total += rate * player.category_simple.split(',').filter(c => c.trim()).length
    const doubleCats = (player.category_double || '').split(',').filter(c => c.trim())
    if (doubleCats.length) {
        total += doubleShare(player.is_licensed) * doubleCats.length // part du joueur
        if (player.partner_name.trim()) total += doubleShare(player.partner_is_licensed)
        if (doubleCats.length >= 2 && player.partner_name_2.trim()) {
            total += doubleShare(player.partner_is_licensed_2)
        }
    }
    return total
}

// Chaque joueur a au moins une catégorie cochée, et aucune bloquée (déjà inscrit, non
// éligible...) : sinon le bouton de validation n'est pas cliquable.
const adminPlayersHaveAllowedCategory = computed(() =>
    adminPlayers.value.every(p => (p.category_simple || p.category_double) && !blockedSelectionError(p))
)

const adminTotalPrice = computed(() =>
    adminPlayers.value.reduce((sum, p) => sum + getAdminPlayerPrice(p), 0)
)

const getLinkedUserId = (ins: any): number | null => ins.linked_user_id ?? null


// Tarif réellement configuré sur l'événement (event.price_cents / price_cents_unlicensed,
// en euros ici) -- plus de barème fixe 6€/8€ codé en dur, sinon ce total n'a plus rien à
// voir avec ce qui a réellement été facturé via HelloAsso.
const licensedRate = computed(() => (event.value?.price_cents ?? 0) / 100)
// Un seul tarif à afficher/éditer : tournoi local (pas de notion de licence) ou coupe
// (aucun non licencié accepté, cf. Dartshub events/routes.py::cup_scope).
const singleRate = computed(() => isLocal.value || !!event.value?.cup_scope)
const unlicensedRate = computed(() => (event.value?.price_cents_unlicensed ?? event.value?.price_cents ?? 0) / 100)
// Prix par équipe des doubles (null hors mode équipe) et part d'un joueur pour une
// catégorie double, en euros -- cf. eventDisplay.ts::doubleShareCents.
const eventTeamPrices = computed(() => teamPrices(event.value))
const doubleShare = (isLicensed: boolean | null | undefined): number =>
    doubleShareCents(event.value, isLicensed, (isLicensed ? licensedRate.value : unlicensedRate.value) * 100) / 100

const getInscriptionPrice = (ins: Inscription): number => {
    // Statut retenu à l'inscription, calculé par le serveur (toutes les orthographes
    // historiques de "Non licencié" -- "NL", "Non-licencié"... -- y sont reconnues).
    const isLicensed = !!ins.is_licensed
    const rate = isLicensed ? licensedRate.value : unlicensedRate.value
    let total = 0
    if (ins.category_simple) total += rate * ins.category_simple.split(',').filter(c => c.trim()).length
    if (ins.category_double) total += doubleShare(isLicensed) * ins.category_double.split(',').filter(c => c.trim()).length
    return total
}

const playerDebts = computed((): DebtGroup[] => {
    const groups = new Map<string, DebtGroup>()

    for (const ins of inscriptions.value) {
        const pKey = playerKey(ins.name, ins.firstname)
        const amount = getInscriptionPrice(ins)

        const cats: string[] = []
        if (ins.category_simple) {
            cats.push(...ins.category_simple.split(',').map(c =>
                SIMPLE_CATEGORIES.find(sc => sc.value === c.trim())?.short || c.trim()
            ))
        }
        if (ins.category_double) {
            cats.push(...ins.category_double.split(',').map(c => c.trim()).map(c =>
                DOUBLE_CATEGORIES.find(dc => dc.value === c)?.short || c
            ))
        }
        const catStr = [...new Set(cats)].join(', ')

        const payInfo = inscriptionPayments.value.find(p => p.inscription_id === ins.id)
        const paymentAmount = (payInfo?.amount_paid_cents ?? 0) / 100
        const isPaid = paymentAmount >= amount
        const paidByName = payInfo?.paid_by_name || null
        const paidByUserId = payInfo?.paid_by_user_id ?? null
        const onsiteDue = (payInfo?.amount_due_onsite_cents ?? 0) / 100
        const onsitePaid = (payInfo?.amount_paid_onsite_cents ?? 0) / 100

        const playerInfo = { name: ins.name, firstname: ins.firstname, categories: catStr, amount }

        if (groups.has(pKey)) {
            const entry = groups.get(pKey)!
            entry.total += amount
            entry.players[0].categories = [...new Set([...entry.players[0].categories.split(', '), ...catStr.split(', ')])].join(', ')
            entry.players[0].amount += amount
            entry.paymentAmount = (entry.paymentAmount || 0) + paymentAmount
            entry.isPaid = entry.isPaid && isPaid
            entry.onsiteDue += onsiteDue
            entry.onsitePaid += onsitePaid
            if (paidByName && paidByName !== `${ins.firstname} ${ins.name}`) entry.paidByName = paidByName
            if (paidByUserId != null) entry.paidByUserId = paidByUserId
        } else {
            const linkedUserId = getLinkedUserId(ins)
            groups.set(pKey, {
                registrantUserId: linkedUserId,
                paidByUserId,
                paymentKey: pKey,
                registrantName: ins.name,
                registrantFirstname: ins.firstname,
                players: [playerInfo],
                total: amount,
                paymentAmount,
                isPaid,
                onsiteDue,
                onsitePaid,
                paidByName: (paidByName && paidByName !== `${ins.firstname} ${ins.name}`) ? paidByName : null
            })
        }
    }

    return [...groups.values()].sort((a, b) =>
        a.registrantName.localeCompare(b.registrantName) ||
        a.registrantFirstname.localeCompare(b.registrantFirstname)
    )
})

// ── Pointage par catégorie ────────────────────────────────────────────────────
// Un tournoi sur plusieurs jours pointe les individuels un jour et les doubles le
// lendemain : chaque catégorie a son propre pointage (Inscription.checked_in_categories
// côté backend, cf. inscriptions/routes.py::set_player_checkin). "Je pointe" choisit les
// catégories concernées ; un joueur n'apparaît que s'il est inscrit dans l'une d'elles.
const CHECKIN_ICONS = { full: '✅', partial: '🟨', none: '⬜' } as const
type CheckinStatus = keyof typeof CHECKIN_ICONS

const splitCats = (v: string | null | undefined): string[] =>
    (v || '').split(',').map(c => c.trim()).filter(Boolean)
const rowCategories = (ins: Inscription): string[] => [...splitCats(ins.category_simple), ...splitCats(ins.category_double)]
const categoryShort = (c: string): string => ALL_CATEGORIES.find(cat => cat.value === c)?.short ?? c
const isDoubleCategory = (c: string): boolean => DOUBLE_CATEGORIES.some(cat => cat.value === c)
const isPresentIn = (ins: Inscription, cat: string): boolean => splitCats(ins.checked_in_categories).includes(cat)
const rowsForGroup = (group: DebtGroup): Inscription[] =>
    inscriptions.value.filter(i => playerKey(i.name, i.firstname) === group.paymentKey)

type PlayerCheckin = { registered: string[]; checked: Set<string> }
const CATEGORY_ORDER: string[] = ALL_CATEGORIES.map(c => c.value)
const checkinByPlayer = computed(() => {
    const map = new Map<string, PlayerCheckin>()
    for (const ins of inscriptions.value) {
        const key = playerKey(ins.name, ins.firstname)
        const entry = map.get(key) ?? { registered: [], checked: new Set<string>() }
        const checked = splitCats(ins.checked_in_categories)
        for (const c of rowCategories(ins)) {
            if (!entry.registered.includes(c)) entry.registered.push(c)
            if (checked.includes(c)) entry.checked.add(c)
        }
        map.set(key, entry)
    }
    for (const entry of map.values()) {
        entry.registered.sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b))
    }
    return map
})
const playerCheckin = (group: DebtGroup): PlayerCheckin =>
    checkinByPlayer.value.get(group.paymentKey) ?? { registered: [], checked: new Set() }

// Catégories qui ont au moins un inscrit : les seules proposées dans "Je pointe".
const pointableCategories = computed(() => ALL_CATEGORIES.filter(cat => getByCategory(cat).length > 0))
// "Je pointe" : les individuels OU les doubles, jamais les deux (individuels par défaut).
// Mémorisé par événement et par navigateur : l'organisateur qui pointe les doubles le
// 2e jour ne doit pas re-sélectionner à chaque rechargement de page.
type PointageKind = 'simple' | 'double'
const pointageKind = ref<PointageKind>('simple')
const kindStorageKey = computed(() => `pointage-kind-${eventId.value}`)
const loadPointageKind = () => {
    let saved: string | null = null
    try { saved = localStorage.getItem(kindStorageKey.value) } catch { /* stockage indisponible */ }
    pointageKind.value = saved === 'double' ? 'double' : 'simple'
}
const setPointageKind = (kind: PointageKind) => {
    pointageKind.value = kind
    try { localStorage.setItem(kindStorageKey.value, kind) } catch { /* stockage indisponible */ }
}
const pointableSimples = computed(() => pointableCategories.value.filter(c => !isDoubleCategory(c.value)).map(c => c.value))
const pointableDoubles = computed(() => pointableCategories.value.filter(c => isDoubleCategory(c.value)).map(c => c.value))
// Type effectivement pointé : celui choisi, sauf s'il n'a aucun inscrit.
const pointageMode = computed<PointageKind>(() => {
    if (pointageKind.value === 'double') return pointableDoubles.value.length || !pointableSimples.value.length ? 'double' : 'simple'
    return pointableSimples.value.length || !pointableDoubles.value.length ? 'simple' : 'double'
})
const pointageScope = computed(() => pointageMode.value === 'double' ? pointableDoubles.value : pointableSimples.value)

const scopedCats = (group: DebtGroup): string[] =>
    playerCheckin(group).registered.filter(c => pointageScope.value.includes(c))
const checkinStatus = (group: DebtGroup): CheckinStatus => {
    const scoped = scopedCats(group)
    const checked = scoped.filter(c => playerCheckin(group).checked.has(c)).length
    if (scoped.length && checked === scoped.length) return 'full'
    return checked ? 'partial' : 'none'
}

const pointageList = computed(() => playerDebts.value.filter(g => scopedCats(g).length > 0))
const pointageCheckedCount = computed(() => pointageList.value.filter(g => checkinStatus(g) === 'full').length)
const pointageFilter = ref<'all' | 'checked' | 'unchecked' | 'onsite_due'>('all')
const hasOnsitePayments = computed(() => playerDebts.value.some(g => g.onsiteDue > 0 || g.onsitePaid > 0))
const totalOnsiteDue = computed(() => playerDebts.value.reduce((sum, g) => sum + g.onsiteDue, 0))

// Affichage du pointage : individuels => joueurs seuls, sans leur doublette ; doubles =>
// une ligne par équipe (les deux joueurs se présentent ensemble), pointée/encaissée d'un
// coup. Les exports restent par joueur (pointageList/checkinStatus ci-dessus).
const groupByKey = computed(() => new Map(playerDebts.value.map(g => [g.paymentKey, g])))
// Membres d'une ligne du pointage : le joueur, plus son partenaire en mode Doubles.
const rowMembers = (group: DebtGroup): DebtGroup[] => {
    if (pointageMode.value !== 'double') return [group]
    const partner = getPrimaryPartner(group.registrantName, group.registrantFirstname)
    const partnerGroup = partner ? groupByKey.value.get(playerKey(partner.name, partner.firstname)) : undefined
    const members = partnerGroup && partnerGroup !== group ? [group, partnerGroup] : [group]
    return members.sort((a, b) => a.registrantName.localeCompare(b.registrantName) || a.registrantFirstname.localeCompare(b.registrantFirstname))
}
const rowLabel = (group: DebtGroup): string =>
    rowMembers(group).map(m => `${m.registrantFirstname} ${m.registrantName}`).join(' / ')
// Partenaire lié (doublette) mais pas inscrit lui-même dans la catégorie double : rien à
// pointer pour lui. Il comptait "non pointé" et la ligne restait en 🟨 quoi qu'on fasse
// (Aurore Pauchet / Cécile Ance, Open de comité La Ciotat 2026) : on l'ignore pour le
// statut et on signale l'incohérence, à corriger dans l'onglet Inscriptions.
const rowUnregisteredMembers = (group: DebtGroup): DebtGroup[] =>
    pointageMode.value === 'double' ? rowMembers(group).filter(m => !scopedCats(m).length) : []
const rowWarning = (group: DebtGroup): string => {
    const missing = rowUnregisteredMembers(group)
    return missing.length
        ? `${missing.map(m => `${m.registrantFirstname} ${m.registrantName}`).join(', ')} : lié(e) à la doublette mais pas inscrit(e) en ${rowCats(group).map(categoryShort).join(', ')}. Corriger la doublette dans l'onglet Inscriptions, sinon l'équipe ne sortira pas dans l'export des doubles.`
        : ''
}
const rowStatus = (group: DebtGroup): CheckinStatus => {
    const missing = rowUnregisteredMembers(group)
    const statuses = rowMembers(group).filter(m => !missing.includes(m)).map(checkinStatus)
    if (statuses.every(s => s === 'full')) return 'full'
    return statuses.every(s => s === 'none') ? 'none' : 'partial'
}
// Pastilles : seulement les catégories pointées (communes à l'équipe en mode Doubles).
const rowCats = scopedCats
const rowCatChecked = (group: DebtGroup, c: string): boolean =>
    rowMembers(group).filter(m => playerCheckin(m).registered.includes(c)).every(m => playerCheckin(m).checked.has(c))
const setRowCatCheckIn = (group: DebtGroup, c: string) => {
    setCheckIn(group, [c], !rowCatChecked(group, c))
}
const rowOnsiteDue = (group: DebtGroup): number => rowMembers(group).reduce((s, m) => s + m.onsiteDue, 0)
const rowOnsitePaid = (group: DebtGroup): number => rowMembers(group).reduce((s, m) => s + m.onsitePaid, 0)

const filteredPlayerDebts = computed(() => {
    let list = pointageList.value
    if (pointageMode.value === 'double') {
        const seen = new Set<string>()
        list = list.filter(g => {
            if (seen.has(g.paymentKey)) return false
            rowMembers(g).forEach(m => seen.add(m.paymentKey))
            return true
        })
    }
    const q = normalize(search.value)
    if (q) list = list.filter(g => rowMembers(g).some(m =>
        normalize(m.registrantName).includes(q) ||
        normalize(m.registrantFirstname).includes(q)
    ))
    // "Non pointés" inclut les partiellement pointés : il leur reste une catégorie à pointer.
    if (pointageFilter.value === 'checked') list = list.filter(g => rowStatus(g) === 'full')
    else if (pointageFilter.value === 'unchecked') list = list.filter(g => rowStatus(g) !== 'full')
    else if (pointageFilter.value === 'onsite_due') list = list.filter(g => rowOnsiteDue(g) > 0)
    return [...list].sort((a, b) => {
        const aC = rowStatus(a) === 'full' ? 1 : 0
        const bC = rowStatus(b) === 'full' ? 1 : 0
        if (aC !== bC) return aC - bC
        return rowLabel(a).localeCompare(rowLabel(b))
    })
})
const pointageRowCount = computed(() => pointageMode.value === 'double'
    ? new Set(pointageList.value.map(g => rowMembers(g).map(m => m.paymentKey).join('|'))).size
    : pointageList.value.length)
const pointageRowCheckedCount = computed(() => pointageMode.value === 'double'
    ? new Set(pointageList.value.filter(g => rowStatus(g) === 'full').map(g => rowMembers(g).map(m => m.paymentKey).join('|'))).size
    : pointageCheckedCount.value)

const normalizeName = (str: string) => str.trim().toUpperCase()

const normalizing = ref(false)
const normalizeAllNames = async () => {
    const accepted = await confirm('Normaliser les noms et prénoms de toutes les inscriptions de cet événement ?');
    if (!accepted) return;

    normalizing.value = true
    let count = 0
    try {
        for (const ins of inscriptions.value) {
            const newName = normalizeName(ins.name)
            const newFirstname = normalizeFirstname(ins.firstname)
            if (newName !== ins.name || newFirstname !== ins.firstname) {
                await backendApi.patch(`/inscriptions/${ins.id}`, { name: newName, firstname: newFirstname }, { headers: { Authorization: `Bearer ${authStore.token}` } })
                count++
            }
        }
        toast.success(`${count} inscription(s) normalisée(s).`)
        await fetchInscriptions()
    } catch (err) {
        handleError(err, 'normalizing names')
    } finally {
        normalizing.value = false
    }
}

const uniquePlayerCount = computed(() => {
    const seen = new Set<string>()
    for (const ins of inscriptions.value) {
        seen.add(`${ins.name.trim().toUpperCase()}|${ins.firstname.trim().toLowerCase()}`)
    }
    return seen.size
})

const getByCategory = (cat: typeof ALL_CATEGORIES[number]) => {
    if (cat.isDouble) {
        const list = inscriptions.value.filter(i =>
            i.category_double?.split(',').map(c => c.trim()).includes(cat.value)
        )
        // Dédupliquer les paires : garder un seul représentant par paire
        const seen = new Set<number>()
        return list.filter(i => {
            if (seen.has(i.id)) return false
            seen.add(i.id)
            if (i.doublette) seen.add(i.doublette)
            return true
        })
    }
    return inscriptions.value.filter(i => i.category_simple?.split(',').map(c => c.trim()).includes(cat.value))
}

// "NOM Prénom (CODE)" -- code absent pour un tournoi local.
const getPlayerLabel = (ins: Inscription): string =>
    `${ins.name} ${ins.firstname}${ins.display_code ? ` (${ins.display_code})` : ''}`

// "NOM Prénom (CODE) / NOM Prénom (CODE)", les deux joueurs par ordre alphabétique du
// nom puis du prénom (cf. orderPair) ; "-" à la place du partenaire tant qu'il n'est pas
// trouvé.
const getDoubletteLabel = (ins: Inscription): string => {
    const partner = ins.doublette ? inscriptions.value.find(i => i.id === ins.doublette) : null
    if (!partner) return `${getPlayerLabel(ins)} / -`
    const [p1, p2] = orderPair(ins, partner)
    return `${getPlayerLabel(p1)} / ${getPlayerLabel(p2)}`
}

const getFilteredByCategory = (cat: typeof ALL_CATEGORIES[number]) => {
    let list = getByCategory(cat)

    const catQ = normalize(globalSearch.value)
    if (catQ) {
        list = list.filter(i => {
            if (normalize(i.name).includes(catQ) || normalize(i.firstname).includes(catQ)) return true
            if (cat.isDouble) {
                const pid = i.doublette
                if (pid) {
                    const partner = inscriptions.value.find(p => p.id === pid)
                    if (partner && (normalize(partner.name).includes(catQ) || normalize(partner.firstname).includes(catQ))) return true
                }
            }
            return false
        })
    }

    if (cat.isDouble) {
        const seen = new Set<number>()
        list = list.filter(i => {
            if (seen.has(i.id)) return false
            seen.add(i.id)
            if (i.doublette) seen.add(i.doublette)
            return true
        })
    }

    return list
}

const fetchEvent = async () => {
    try {
        const { data } = await backendApi.get(`/events/${eventId.value}`)
        event.value = data
        const limits = data.category_limits ?? {}
        categoryLimits.value = Object.fromEntries(
            ALL_CATEGORIES.map(cat => [cat.value, limits[cat.value] ?? null])
        )
        // open_categories null = toutes ouvertes (comportement par défaut) -- toutes les
        // cases partent cochées dans ce cas, comme pour un tournoi qui n'a encore jamais
        // été restreint.
        const openList: string[] | null = data.open_categories ?? null
        openCategories.value = Object.fromEntries(
            ALL_CATEGORIES.map(cat => [cat.value, openList === null || openList.includes(cat.value)])
        )
        fillInfoForm()
        fillPricingForm()
        if (isLinkedToBadarts.value) {
            if (!isTabId(route.query.tab) && !visibleTabs.value.some(t => t.id === activeTab.value)) {
                activeTab.value = 'roster'
            }
            await fetchBadartsRoster()
        }
    } catch (err) {
        handleError(err, 'fetching event')
    }
}

// `mode` ('single'|'double', fixe pour tout le tournoi Badarts -- pas de catégorie par
// joueur comme sur DartsHub) conditionne quels formats/fichiers d'export ont un sens,
// cf. EXPORT_SOFTWARES plus bas.
const badartsRoster = ref<any[]>([])
const badartsMode = ref<'single' | 'double' | null>(null)
const badartsRosterLoading = ref(false)
const fetchBadartsRoster = async () => {
    badartsRosterLoading.value = true
    try {
        const { data } = await backendApi.get(`/events/${eventId.value}/badarts-roster`, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        badartsRoster.value = data.players ?? []
        badartsMode.value = data.mode ?? null
    } catch (err) {
        handleError(err, 'récupération des inscrits Badarts')
    } finally {
        badartsRosterLoading.value = false
    }
}

// Doublette Badarts : deux inscrits qui partagent le même participant_id (une "Participant"
// slot = 1 ou 2 joueurs selon tournament.mode), contrairement à DartsHub où chaque
// inscription porte son propre id de partenaire (Inscription.doublette). Chaque paire
// est ordonnée par nom puis prénom affichés (cf. orderPair, badartsPlayerName).
const badartsPairs = computed<[any, any][]>(() => {
    const byParticipant = new Map<number, any[]>()
    for (const p of badartsRoster.value) {
        if (p.participant_id == null) continue
        if (!byParticipant.has(p.participant_id)) byParticipant.set(p.participant_id, [])
        byParticipant.get(p.participant_id)!.push(p)
    }
    return [...byParticipant.values()]
        .filter(g => g.length === 2)
        .map(([a, b]) => comparePlayerNames(badartsPlayerName(a), badartsPlayerName(b)) <= 0 ? [a, b] : [b, a]) as [any, any][]
})

const fetchInscriptions = async () => {
    try {
        const { data } = await backendApi.get('/inscriptions/active', {
            params: { event_id: eventId.value },
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        inscriptions.value = data
    } catch (err) {
        handleError(err, 'fetching inscriptions')
    }
}

const fetchAdminInscriptionPayments = async () => {
    try {
        const { data } = await backendApi.get(`/events/admin-inscription-payments/${eventId.value}`, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        inscriptionPayments.value = data
    } catch (err) {
        console.warn('Erreur chargement paiements inscriptions', err)
        inscriptionPayments.value = []
    }
}


const deleteDoublette = async (inscriptionId: number, category?: string) => {
    const accepted = await confirm('Supprimer la doublette pour les 2 joueurs ? Leurs inscriptions individuelles seront conservées.');
    if (!accepted) return;
    try {
        const params = category ? { params: { category } } : {}
        await backendApi.delete(`/inscriptions/${inscriptionId}/doublette`, {
            headers: { Authorization: `Bearer ${authStore.token}` },
            ...params,
        })
        toast.success('Doublette supprimée.')
        await fetchInscriptions()
    } catch {
        toast.error('Erreur lors de la suppression de la doublette.')
    }
}

// Changement de catégorie individuelle sur la même ligne (joueur inscrit dans la
// mauvaise catégorie) : plus besoin de supprimer puis refaire l'inscription, ce qui
// perdait le paiement d'origine (cf. Dartshub inscriptions/routes.py::change_simple_category,
// qui applique les contrôles d'éligibilité, de cumul et de places).
const changingCategory = ref<{ id: number, from: string, to: string } | null>(null)
const changingCategorySaving = ref(false)
const categoryTargets = (ins: Inscription, from: string) =>
    openSimpleCategories.value.filter(c => c.value !== from && !splitCats(ins.category_simple).includes(c.value))
const startCategoryChange = (ins: Inscription, from: string) => {
    changingCategory.value = { id: ins.id, from, to: categoryTargets(ins, from)[0]?.value ?? '' }
}
const submitCategoryChange = async () => {
    const change = changingCategory.value
    if (!change?.to) return
    changingCategorySaving.value = true
    try {
        await backendApi.post(`/inscriptions/${change.id}/change-category`,
            { from_category: change.from, to_category: change.to },
            { headers: { Authorization: `Bearer ${authStore.token}` } })
        const label = (v: string) => SIMPLE_CATEGORIES.find(c => c.value === v)?.short ?? v
        toast.success(`Catégorie changée : ${label(change.from)} → ${label(change.to)}.`)
        changingCategory.value = null
        await fetchInscriptions()
    } catch (err: any) {
        toast.error(err?.response?.data?.detail || 'Erreur lors du changement de catégorie.')
    } finally {
        changingCategorySaving.value = false
    }
}

const deleteInscription = async (inscriptionId: number, category?: string) => {
    const player = inscriptions.value.find(i => i.id === inscriptionId)
    const catLabel = category ? SIMPLE_CATEGORIES.find(c => c.value === category)?.label : null
    const accepted = await confirm(
        catLabel
            ? `Supprimer l'inscription de ${player?.firstname} ${player?.name} en ${catLabel} ?`
            : `Supprimer l'inscription de ${player?.firstname} ${player?.name} ?`
    );
    if (!accepted) return;
    try {
        const params = category ? { params: { category } } : {}
        await backendApi.delete(`/inscriptions/${inscriptionId}`, {
            headers: { Authorization: `Bearer ${authStore.token}` },
            ...params,
        })
        toast.success('Inscription supprimée.')
        await fetchInscriptions()
    } catch (err) {
        handleError(err, 'deleting inscription')
    }
}

// Pointe/dépointe `cats` pour ce joueur, et pour son partenaire dans les catégories
// doubles : une double se pointe par équipe, en une seule requête (le serveur pointe les
// deux joueurs ensemble, cf. inscriptions/routes.py::set_player_checkin). Mise à jour
// optimiste des lignes chargées (le pointage est dérivé de checked_in_categories),
// annulée si le serveur refuse.
const setCheckIn = async (group: DebtGroup, cats: string[], newState: boolean) => {
    if (!canManage.value || !cats.length) return
    const own = rowsForGroup(group)
    const targets = new Map<Inscription, string[]>(own.map(r => [r, cats]))
    for (const r of own) {
        const partner = r.doublette ? inscriptions.value.find(i => i.id === r.doublette) : undefined
        const doubleCats = cats.filter(c => splitCats(r.category_double).includes(c))
        if (partner && doubleCats.length && !targets.has(partner)) targets.set(partner, doubleCats)
    }
    const rows = [...targets.keys()]
    const previous = rows.map(r => r.checked_in_categories)
    for (const [r, rCats] of targets) {
        const registered = rowCategories(r)
        const current = new Set(splitCats(r.checked_in_categories).filter(c => registered.includes(c)))
        for (const c of rCats) {
            if (!registered.includes(c)) continue
            if (newState) current.add(c)
            else current.delete(c)
        }
        r.checked_in_categories = [...current].sort().join(',') || null
        r.checked_in = current.size > 0
    }
    try {
        const { data } = await backendApi.patch<{ updated: number }>('/inscriptions/checkin/' + eventId.value, null, {
            params: { name: group.registrantName, firstname: group.registrantFirstname, checked_in: newState, categories: cats.join(',') },
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        // Aucune ligne retrouvée côté serveur : l'affichage mentait (Éric Le Serrec,
        // affiché pointé alors que rien n'était enregistré). On annule et on prévient.
        if (!data?.updated) throw new Error(`${group.registrantFirstname} ${group.registrantName} : pointage non enregistré par le serveur.`)
    } catch (err) {
        rows.forEach((r, i) => {
            r.checked_in_categories = previous[i]
            r.checked_in = !!previous[i]
        })
        handleError(err, 'updating check-in')
    }
}

// Clic sur la case du joueur : pointe toutes ses catégories sélectionnées ("Je pointe"),
// ou les dépointe toutes s'il l'était déjà entièrement.
// Encaissement sur place (ou annulation) de toutes les inscriptions du joueur -- le back
// ne touche que les paiements "onsite" (cf. events/routes.py::set_onsite_payment).
const collecting = ref(false)
const setOnsitePaid = async (group: DebtGroup, paid: boolean) => {
    if (!paid && !(await confirm(`Annuler l'encaissement sur place de ${rowLabel(group)} ?`))) return
    collecting.value = true
    try {
        await backendApi.patch(`/events/${eventId.value}/onsite-payment`, {
            inscription_ids: rowMembers(group).flatMap(m => rowsForGroup(m).map(i => i.id)),
            paid,
        }, { headers: { Authorization: `Bearer ${authStore.token}` } })
        await fetchAdminInscriptionPayments()
        toast.success(paid ? `Paiement encaissé : ${rowLabel(group)}` : 'Encaissement annulé')
    } catch (err) {
        handleError(err, 'updating onsite payment')
    } finally {
        collecting.value = false
    }
}

// Le partenaire suit (cf. setCheckIn) : une seule requête pour l'équipe.
const toggleCheckIn = (group: DebtGroup) => setCheckIn(group, scopedCats(group), rowStatus(group) !== 'full')

// Plus de bouton "Supprimer les absents" : le pointage suffit à filtrer tous les exports,
// et supprimer les inscriptions effaçait aussi leur paiement (plus de trace de qui avait
// payé sans venir) et faussait le taux de présence des stats. Un retrait ponctuel se fait
// inscription par inscription depuis l'onglet Inscriptions.

// Un joueur n'a jamais qu'un seul partenaire de doublette en base (Inscription.doublette
// est un champ unique, pas une liste). Utilisé par le pointage des doubles et les exports
// 3K Darts / DartConnect qui n'ont qu'une seule colonne partenaire.
const getPrimaryPartner = (name: string, firstname: string): Inscription | null => {
    const ins = inscriptions.value.find(i =>
        i.name.toUpperCase() === name.toUpperCase() && i.firstname.toLowerCase() === firstname.toLowerCase() && i.doublette
    )
    if (!ins?.doublette) return null
    return inscriptions.value.find(i => i.id === ins.doublette) ?? null
}

const confirmDeleteAll = async () => {
    const accepted = await confirm(`Supprimer TOUTES les inscriptions de "${event.value?.display_name || event.value?.name}" ? Cette action est irréversible.`);
    if (!accepted) return;
    const acceptedbis = await confirm(`Êtes vous vraiment certain ?`);
    if (!acceptedbis) return;
    try {
        await backendApi.delete(`/inscriptions/event/${eventId.value}`, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        toast.success('Toutes les inscriptions ont été supprimées.')
        await fetchInscriptions()
    } catch (err) {
        handleError(err, 'deleting all inscriptions')
    }
}

// Lu sur le store à chaque appel (pas via une copie prise au setup : load() n'est pas
// attendu, la copie restait vide au premier affichage de la page et les exports
// sortaient alors le club tel que stocké -- code pour les uns, nom complet pour les
// autres). Recherche par NOM via clubCodeByName (clubMapping, indexé par code, perd les
// clubs dont le code est partagé : La Seyne/SDC, Crau'Darts/CDC...), insensible à la
// casse, aux accents, apostrophes/tirets et au suffixe de comité "Club (MER)" que
// certaines voies d'inscription ajoutent (cf. backend inscriptions/club_display.py).
const clubNameKey = (s: string): string =>
    normalize(s).replace(/\s*\([^)]*\)\s*$/, '').replace(/['’-]/g, ' ').replace(/\s+/g, ' ').trim()
const getClubCode = (club: string): string => {
    if (clubsStore.clubMapping[club]) return club
    const key = clubNameKey(club)
    const entry = Object.entries(clubsStore.clubCodeByName).find(([name]) => clubNameKey(name) === key)
    return entry ? entry[1] : club
}


// Open Fédéral : licence non retrouvée chez nous ne veut pas dire non licencié (autres
// ligues), donc jamais "NL" -- cf. backend inscriptions/club_display.py::is_open_federal.
const isOpenFederal = computed(() => normalize(event.value?.name || '').startsWith('open federal'))

const getClubAbbr = (club: string): string => {
    const code = getClubCode(club)
    // Même liste que is_non_licensed_club côté serveur (events/models.py).
    const key = (code || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/-/g, ' ').trim().toUpperCase()
    if (['', 'NL', 'NON LICENCIE', 'NON RENSEIGNE'].includes(key)) return isOpenFederal.value ? '' : 'NL'
    return code
}

const SIMPLE_CAT_LABEL: Record<string, string> = { M: 'Masculin', V: 'Vétéran', F: 'Féminin', J: 'Junior' }

const playerKey = (name: string, firstname: string): string =>
    `${normalize(name)}|${normalize(firstname)}`

const showExportModal = ref(false)
const exportSoftware = ref<'d4w' | '3kdarts' | 'dartsconnect' | 'dartup'>('d4w')
const exportCategory = ref<'individual' | 'doubles'>('individual')

const EXPORT_SOFTWARES = [
    { id: 'd4w', label: 'Darts4Windows', shortLabel: 'D4W', logo: darts4windowsLogo },
    { id: '3kdarts', label: '3K Darts', shortLabel: '3K', logo: threeKDartsLogo },
    { id: 'dartsconnect', label: 'DartsConnect', shortLabel: 'DC', logo: dartConnectLogo },
    { id: 'dartup', label: 'DartUP', shortLabel: 'DU', logo: dartUpLogo },
] as const

const EXPORT_CATEGORIES = [
    { id: 'individual', label: 'Individuels présents', labelAll: 'Tous les individuels' },
    { id: 'doubles', label: 'Doubles présents', labelAll: 'Toutes les doublettes' },
] as const

// Export "tous les inscrits" (super_admin) : mêmes formats, mais sans filtre de pointage
// ni de "Je pointe" -- ex. pour préparer le logiciel avant l'ouverture du pointage.
const exportAll = ref(false)
const openExport = (all: boolean) => {
    exportAll.value = all && isSuperAdmin.value
    showExportModal.value = true
}
const exportPresent = (ins: Inscription, cat: string): boolean => exportAll.value || isPresentIn(ins, cat)
const exportSuffix = computed(() => (exportAll.value ? 'tous' : 'presents'))

// Darts4Windows et DartUP séparent individuel/double en deux fichiers -- 3K Darts et
// DartsConnect exportent tous les présents en une seule fois (le partenaire de
// doublette est une colonne, pas un fichier séparé), donc pas de choix de catégorie
// pour eux.
const exportNeedsCategory = computed(() => exportSoftware.value === 'd4w' || exportSoftware.value === 'dartup')

const runExport = async () => {
    // Event lié Badarts (onglet Participants) : source = badartsRoster/badartsMode, pas
    // les inscriptions/le pointage DartsHub (inexistants ici, cf. isLinkedToBadarts).
    if (isLinkedToBadarts.value) {
        if (exportSoftware.value === 'd4w') {
            if (exportCategory.value === 'individual') exportBadartsD4WIndividualCSV()
            else exportBadartsD4WDoublesCSV()
        } else if (exportSoftware.value === '3kdarts') {
            exportBadarts3KDartsCSV()
        } else if (exportSoftware.value === 'dartsconnect') {
            exportBadartsDartConnectXlsx()
        } else if (exportSoftware.value === 'dartup') {
            if (exportCategory.value === 'individual') exportBadartsDartUpIndividualXlsx()
            else exportBadartsDartUpDoublesXlsx()
        }
        showExportModal.value = false
        return
    }
    // Pointage fait sur plusieurs appareils : la liste chargée sur celui qui exporte peut
    // dater, rechargée avant chaque export pour ne perdre aucune équipe pointée ailleurs.
    await fetchInscriptions()
    if (exportSoftware.value === 'd4w') {
        if (exportCategory.value === 'individual') exportDarts4WindowsIndividualCSV()
        else exportDarts4WindowsDoublesCSV()
    } else if (exportSoftware.value === '3kdarts') {
        export3KDartsCSV()
    } else if (exportSoftware.value === 'dartsconnect') {
        exportDartConnectXlsx()
    } else if (exportSoftware.value === 'dartup') {
        if (exportCategory.value === 'individual') exportDartUpIndividualXlsx()
        else exportDartUpDoublesXlsx()
    }
    showExportModal.value = false
}

// Type/genre D4W de chaque joueur, 1re colonne de l'import individuel et 2e des lignes
// joueur des paires (https://www.dartsforwin.com/howto/importcsv.html) : 0 homme,
// 1 femme, 2 garçon, 3 fille. Femme si sa licence le dit (seule donnée de licence
// utilisée dans un export, et seulement ici -- cf. Dartshub events/routes.py::
// get_female_inscription_ids), ou à défaut si le joueur est inscrit en Individuel
// Féminin / Double Féminin ; junior s'il est inscrit en Individuel Junior -- sur
// n'importe laquelle de ses lignes d'inscription. Sinon homme (0), comme le fait D4W
// par défaut.
const fetchD4WGender = async (): Promise<((ins: Inscription) => number) | null> => {
    let femaleIds: Set<number>
    try {
        const { data } = await backendApi.get<number[]>(`/events/${eventId.value}/inscriptions-female`, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        femaleIds = new Set(data)
    } catch (err) {
        handleError(err, 'récupération du genre des inscrits')
        return null
    }
    const women = new Set<string>()
    const juniors = new Set<string>()
    for (const i of inscriptions.value) {
        const key = playerKey(i.name, i.firstname)
        if (femaleIds.has(i.id) || splitCats(i.category_simple).includes('F') || splitCats(i.category_double).includes('DF')) women.add(key)
        if (splitCats(i.category_simple).includes('J')) juniors.add(key)
    }
    return ins => {
        const key = playerKey(ins.name, ins.firstname)
        return (juniors.has(key) ? 2 : 0) + (women.has(key) ? 1 : 0)
    }
}

const exportDarts4WindowsIndividualCSV = async () => {
    const genderOf = await fetchD4WGender()
    if (!genderOf) return
    const rows: string[] = []
    const seen = new Set<string>()

    for (const ins of inscriptions.value) {
        if (!ins.category_simple) continue
        for (const cat of ins.category_simple.split(',').map(c => c.trim())) {
            if (!['M', 'F', 'V', 'J'].includes(cat) || !exportPresent(ins, cat)) continue
            const label = SIMPLE_CAT_LABEL[cat] || cat
            const key = `${ins.name.toUpperCase()}|${ins.firstname.toLowerCase()}|${label}`
            if (seen.has(key)) continue
            seen.add(key)
            // Genre "junior" (2/3) sur la seule ligne Junior : D4W range ses listes par
            // genre, un junior inscrit aussi en Masculin n'apparaissait que chez les
            // juniors. Il sort donc deux fois, adulte (0/1) en Masculin, junior en Junior.
            const gender = cat === 'J' ? genderOf(ins) : genderOf(ins) % 2
            rows.push([gender, ins.firstname, ins.name, getClubAbbr(ins.club), '', '', '', '', label].join(';'))
        }
    }

    if (!rows.length) { toast.info('Aucun joueur présent en individuel.'); return }

    const csv = '\uFEFF' + rows.join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${event.value?.display_name || event.value?.name || 'event'}_individuel_${exportSuffix.value}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// Format d'import des paires Darts4Windows (voir ExpPairsMixedPairs13102026.csv fourni par
// l'utilisateur, export natif de D4W) : 3 lignes par paire -- un en-tête d'équipe
// "0;<type>;<équipe>;<équipe>;<code 3 car.>;1" (équipe = club du joueur 1), puis chaque
// joueur "<1|2>;<genre>;<prénom>;<nom>;<club>;<code 3 car.>;;" (genre : cf.
// fetchD4WGender). Pas de "tournament filter" pour les paires (colonne ajoutée ignorée,
// testé) : la catégorie passe par le type de la paire, que la fenêtre d'ajout au tournoi
// sait filtrer ("<all types>"), mêmes codes que le genre joueur : Double Mixte = 0
// (hommes : ouvert à tous, et c'est le filtre "Men" qui l'affiche -- le 5 du modèle
// "MixedPairs" n'est retenu par aucun des filtres hommes/femmes), Double Féminin = 1
// (femmes, vérifié à l'import). Toutes les catégories doubles vont donc dans
// un seul fichier.
// Encodage Windows-1252 sans BOM, fins de ligne CRLF (y compris la dernière), comme
// l'original.
const D4W_PAIR_TYPE: Record<string, number> = { DM: 0, DF: 1 }
type D4WPlayer = { firstname: string; name: string; club: string; gender: number }
type D4WPair = { players: [D4WPlayer, D4WPlayer]; type: number }
const d4wClubCode = (club: string): string => club.toUpperCase().slice(0, 3)
const d4wPairRows = ({ players: [p1, p2], type }: D4WPair): string[] => [
    ['0', type, p1.club, p1.club, d4wClubCode(p1.club), '1'].join(';'),
    ...[p1, p2].map((p, i) => [i + 1, p.gender, p.firstname, p.name, p.club, d4wClubCode(p.club), '', ''].join(';')),
]

// Caractères hors Latin-1 présents dans Windows-1252 ; le reste (rare dans un nom) est
// ramené à sa lettre de base plutôt que de casser l'import.
const CP1252_EXTRA: Record<string, number> = {
    '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87, 'ˆ': 0x88, '‰': 0x89,
    'Š': 0x8A, '‹': 0x8B, 'Œ': 0x8C, 'Ž': 0x8E, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95,
    '–': 0x96, '—': 0x97, '˜': 0x98, '™': 0x99, 'š': 0x9A, '›': 0x9B, 'œ': 0x9C, 'ž': 0x9E, 'Ÿ': 0x9F,
}
const encodeCp1252 = (text: string): Uint8Array => {
    const bytes: number[] = []
    for (const ch of text) {
        const code = ch.codePointAt(0)!
        if (code < 0x80 || (code >= 0xA0 && code <= 0xFF)) bytes.push(code)
        else if (CP1252_EXTRA[ch]) bytes.push(CP1252_EXTRA[ch])
        else {
            const base = ch.normalize('NFD').replace(/\p{Diacritic}/gu, '')
            for (const c of base) bytes.push(c.codePointAt(0)! < 0x80 ? c.codePointAt(0)! : 0x3F)
        }
    }
    return new Uint8Array(bytes)
}

const downloadD4WPairs = (pairs: D4WPair[], filename: string) => {
    const content = pairs.flatMap(d4wPairRows).map(r => r + '\r\n').join('')
    const blob = new Blob([encodeCp1252(content).buffer as ArrayBuffer],{ type: 'text/csv;charset=windows-1252;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
}

const exportDarts4WindowsDoublesCSV = async () => {
    const genderOf = await fetchD4WGender()
    if (!genderOf) return
    const pairsByCat = new Map<string, D4WPair[]>()
    const seen = new Set<string>()
    // Joueurs pointés dont l'équipe ne sort pas, avec la raison : affichés après l'export
    // pour qu'aucune équipe ne manque sans qu'on le voie (Jules Pauchet / Christophe
    // Risselin, Open de comité La Ciotat 2026).
    const skipped = new Set<string>()
    const who = (i: Inscription) => `${i.firstname} ${i.name}`

    for (const ins of inscriptions.value) {
        if (!ins.category_double) continue
        const partner = ins.doublette ? inscriptions.value.find(i => i.id === ins.doublette) : undefined

        for (const cat of splitCats(ins.category_double)) {
            const label = categoryShort(cat)
            if (!partner) {
                if (exportPresent(ins, cat)) skipped.add(`${who(ins)} (${label}) : pas de partenaire`)
                continue
            }
            if (!splitCats(partner.category_double).includes(cat)) {
                if (exportPresent(ins, cat)) skipped.add(`${who(ins)} / ${who(partner)} (${label}) : ${who(partner)} pas inscrit(e) en ${label}`)
                continue
            }
            if (!exportPresent(ins, cat) || !exportPresent(partner, cat)) {
                if (exportPresent(ins, cat) !== exportPresent(partner, cat)) {
                    const [p1, p2] = orderPair(ins, partner)
                    skipped.add(`${who(p1)} / ${who(p2)} (${label}) : équipe à moitié pointée`)
                }
                continue
            }
            // Dédoublonnage par PAIRE (les deux inscriptions de la doublette pointent
            // l'une vers l'autre), jamais par joueur seul : un joueur ayant plusieurs
            // lignes d'inscription laisserait sinon son partenaire orphelin.
            const pair = orderPair(ins, partner)
            const key = `${cat}|${pair.map(p => playerKey(p.name, p.firstname)).join('|')}`
            if (seen.has(key)) continue
            seen.add(key)
            const players = pair.map(p => ({ firstname: p.firstname, name: p.name, club: getClubAbbr(p.club), gender: genderOf(p) })) as [D4WPlayer, D4WPlayer]
            pairsByCat.set(cat, [...(pairsByCat.get(cat) ?? []), { players, type: D4W_PAIR_TYPE[cat] ?? 0 }])
        }
    }

    // Regroupées par catégorie, dans l'ordre de DOUBLE_CATEGORIES.
    const pairs = DOUBLE_CATEGORIES.flatMap(c => pairsByCat.get(c.value) ?? [])
    if (skipped.size) {
        toast.warning(`Pointé(s) mais absent(s) du fichier :\n${[...skipped].join('\n')}`, { autoClose: false, bodyClassName: 'toast-multiline' })
    }
    if (!pairs.length) { toast.info('Aucune doublette présente.'); return }
    downloadD4WPairs(pairs, `${event.value?.display_name || event.value?.name || 'event'}_doubles_${exportSuffix.value}.csv`)
    toast.success(`${pairs.length} équipe(s) exportée(s).`)
}

// Format 3K Darts (voir sample3k.csv fourni par l'utilisateur) : une seule ligne par
// joueur présent, contrairement au format Darts4Windows qui sépare individuel/double en
// deux fichiers avec une ligne par catégorie -- ici les colonnes "(Double)" portent déjà
// le partenaire, donc pas besoin de dupliquer la ligne par catégorie. Beaucoup de
// colonnes du modèle (naissance, adresse, téléphone, email du joueur...) ne sont pas des
// données que Compet connaît : laissées vides plutôt que remplies avec des valeurs
// inventées.
const export3KDartsCSV = () => {
    const header = [
        'Firstname', 'Name', 'Gender', 'Birthday', 'Street', 'ZIP', 'City', 'Country',
        'Member number', 'Nickname', 'Username', 'Phone', 'Mobile', 'Mail address',
        'External ID', 'Club', 'Association', 'Team', 'Status', 'Present', 'Paid',
        'Paid date', 'Firstname (Double)', 'Name (Double)', 'Gender (Double)', 'Birthday (Double)',
    ]
    const rows: string[] = [header.join(';')]

    // Présents = pointés dans au moins une des catégories sélectionnées ("Je pointe") :
    // le jour des doubles, on n'exporte que les doubles.
    for (const group of exportAll.value ? playerDebts.value : pointageList.value) {
        const present = exportAll.value
            ? playerCheckin(group).registered
            : scopedCats(group).filter(c => playerCheckin(group).checked.has(c))
        if (!present.length) continue

        const ins = inscriptions.value.find(i =>
            i.name.toUpperCase() === group.registrantName.toUpperCase() &&
            i.firstname.toLowerCase() === group.registrantFirstname.toLowerCase()
        )
        const status = present.map(categoryShort).join(' | ')
        const partner = present.some(isDoubleCategory)
            ? getPrimaryPartner(group.registrantName, group.registrantFirstname)
            : null

        rows.push([
            group.registrantFirstname, group.registrantName, '', '', '', '', '', 'France',
            '', '', '', '', '', '', '', getClubAbbr(ins?.club ?? ''), '', '', status, 1, 1, '',
            partner?.firstname ?? '', partner?.name ?? '', '', '',
        ].join(';'))
    }

    if (rows.length <= 1) { toast.info('Aucun joueur présent.'); return }

    const csv = '﻿' + rows.join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${event.value?.display_name || event.value?.name || 'event'}_3kdarts.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// Format DartConnect (voir DartConnect-Event-Player-Loading-Form-2022.xlsx fourni par
// l'utilisateur) : un classeur avec une feuille "Player Roster " (espace final inclus
// dans leur propre modèle) et cet en-tête exact. Pas de notion de catégorie ni de
// partenaire dans ce modèle -- une ligne par joueur présent suffit.
const exportDartConnectXlsx = async () => {
    const present = exportAll.value
        ? playerDebts.value
        : pointageList.value.filter(g => checkinStatus(g) !== 'none')
    if (!present.length) { toast.info('Aucun joueur présent.'); return }

    // Chargé à la demande : exceljs pèse ~1Mo minifié, pas question de l'embarquer dans
    // le bundle initial pour une fonctionnalité utilisée occasionnellement par quelques
    // organisateurs.
    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet('Player Roster ')
    sheet.addRow(['Org Player ID', 'Gender', 'ISO Country Code', 'EmailAddress', 'First Name', 'Last Name', 'Division (leagues Only)'])
    for (const group of present) {
        sheet.addRow(['', '', 'FRA', '', group.registrantFirstname, group.registrantName, ''])
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${event.value?.display_name || event.value?.name || 'event'}_dartconnect.xlsx`
    a.click()
    URL.revokeObjectURL(url)
}

// Format DartUP (voir participants_test_aza.xlsx / modele_import_doubles.xlsx fournis
// par l'utilisateur) : comme Darts4Windows, deux fichiers séparés individuel/double.
// Contrairement aux trois autres formats (qui n'exportent que les présents), le modèle
// individuel liste TOUS les inscrits avec une colonne Présent et une colonne Payé par
// joueur -- ce n'est donc pas un filtre mais une vraie colonne à remplir. Le modèle
// double n'a lui ni l'une ni l'autre colonne : on n'y exporte donc que les paires dont
// les deux joueurs sont pointés dans une même catégorie double, comme pour
// Darts4Windows. Licence/Email/Sexe/Naissance : TOUJOURS vides -- un export ne sort
// que nom, prénom et club (règle du 2026-10-14, valable pour tous les formats), jamais
// de donnée de licence ni de contact. Ville : laissée vide. Club : nom
// complet (pas de code abrégé, contrairement à Darts4Windows/3K Darts) -- le modèle
// fourni montre un vrai nom de club en exemple.
const DARTUP_INDIVIDUAL_HEADER = ['Nom', 'Prénom', 'Licence', 'Email', 'Sexe', 'Naissance', 'Club', 'Ville', 'Seed', 'Payé', 'Présent']

const exportDartUpIndividualXlsx = async () => {
    const rows: Inscription[] = []
    const seen = new Set<string>()
    for (const ins of inscriptions.value) {
        if (!ins.category_simple) continue
        const key = playerKey(ins.name, ins.firstname)
        if (seen.has(key)) continue
        seen.add(key)
        rows.push(ins)
    }
    if (!rows.length) { toast.info('Aucun joueur inscrit en individuel.'); return }


    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet('Participants')
    sheet.addRow(DARTUP_INDIVIDUAL_HEADER)
    for (const ins of rows) {
        const paid = playerDebts.value.find(g => g.paymentKey === playerKey(ins.name, ins.firstname))?.isPaid ?? false
        const present = splitCats(ins.category_simple).some(c => exportPresent(ins, c))
        sheet.addRow([
            ins.name, ins.firstname, '', '', '', '', ins.club, '', '',
            paid ? 'Oui' : 'Non', present ? 'Oui' : 'Non',
        ])
    }

    await downloadXlsx(workbook, `${event.value?.display_name || event.value?.name || 'event'}_dartup_individuel.xlsx`)
}

const exportDartUpDoublesXlsx = async () => {
    const pairs: [Inscription, Inscription][] = []
    const seenPairs = new Set<number>()
    for (const ins of inscriptions.value) {
        if (!ins.category_double || seenPairs.has(ins.id)) continue
        seenPairs.add(ins.id)
        const partner = ins.doublette ? inscriptions.value.find(i => i.id === ins.doublette) : null
        if (!partner) continue // cherche encore partenaire : rien à mettre en Nom2/Prénom2
        seenPairs.add(partner.id)
        if (!splitCats(ins.category_double).some(cat => exportPresent(ins, cat) && exportPresent(partner, cat))) continue
        pairs.push(orderPair(ins, partner))
    }
    if (!pairs.length) { toast.info('Aucune doublette présente.'); return }

    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet('Sheet')
    sheet.addRow(['Nom1', 'Prénom1', 'Email1', 'Nom2', 'Prénom2', 'Email2', "Nom d'équipe"])
    for (const [p1, p2] of pairs) {
        sheet.addRow([p1.name, p1.firstname, '', p2.name, p2.firstname, '', ''])
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${event.value?.display_name || event.value?.name || 'event'}_dartup_doubles.xlsx`
    a.click()
    URL.revokeObjectURL(url)
}

// ── Exports (event lié Badarts) ─────────────────────────────────────────────────
// Mêmes 4 formats que ci-dessus, mais depuis badartsRoster/badartsMode/badartsPairs :
// pas de catégorie FFD (M/F/V/J) ni de club connus côté Badarts (aucun des deux n'y est
// stocké), donc catégorie fixe "Simple"/"Double" (mode du tournoi) et club toujours vide
// -- jamais inventés. Pas de notion de présence/pointage non plus : TOUS les inscrits
// sont exportés (l'équivalent Badarts de "présent" n'existe pas, cf. isLinkedToBadarts).
const badartsPlayerName = (p: any) => ({ firstname: p.firstname || p.nickname || '', name: p.name || '' })

const exportBadartsD4WIndividualCSV = () => {
    if (badartsMode.value !== 'single') { toast.info('Aucun joueur en individuel (tournoi en mode double).'); return }
    const rows = badartsRoster.value.map(p => {
        const { firstname, name } = badartsPlayerName(p)
        return [0, firstname, name, 'NL', '', '', '', '', 'Simple'].join(';')
    })
    if (!rows.length) { toast.info('Aucun joueur inscrit.'); return }
    downloadCsv(rows, `${event.value?.display_name || event.value?.name || 'event'}_individuel.csv`)
}

const exportBadartsD4WDoublesCSV = () => {
    if (badartsMode.value !== 'double') { toast.info('Aucune doublette (tournoi en mode individuel).'); return }
    if (!badartsPairs.value.length) { toast.info('Aucune doublette inscrite.'); return }
    // Pas de genre connu côté Badarts : 0.
    const pairs = badartsPairs.value.map(pair => ({
        players: pair.map(p => ({ ...badartsPlayerName(p), club: 'NL', gender: 0 })) as [D4WPlayer, D4WPlayer],
        type: 0,
    }))
    downloadD4WPairs(pairs, `${event.value?.display_name || event.value?.name || 'event'}_doubles.csv`)
}

const exportBadarts3KDartsCSV = () => {
    const header = [
        'Firstname', 'Name', 'Gender', 'Birthday', 'Street', 'ZIP', 'City', 'Country',
        'Member number', 'Nickname', 'Username', 'Phone', 'Mobile', 'Mail address',
        'External ID', 'Club', 'Association', 'Team', 'Status', 'Present', 'Paid',
        'Paid date', 'Firstname (Double)', 'Name (Double)', 'Gender (Double)', 'Birthday (Double)',
    ]
    const rows = [header.join(';')]
    for (const p of badartsRoster.value) {
        const { firstname, name } = badartsPlayerName(p)
        const partner = badartsMode.value === 'double'
            ? badartsPairs.value.find(pair => pair[0].id === p.id || pair[1].id === p.id)?.filter(m => m.id !== p.id)[0]
            : null
        rows.push([
            firstname, name, '', '', '', '', '', 'France',
            '', p.nickname || '', '', '', '', p.email || '', '', 'NL', '', '',
            badartsMode.value === 'double' ? 'Double' : 'Simple', 1, p.paid ? 1 : 0, '',
            partner ? badartsPlayerName(partner).firstname : '', partner ? badartsPlayerName(partner).name : '', '', '',
        ].join(';'))
    }
    if (rows.length <= 1) { toast.info('Aucun joueur inscrit.'); return }
    downloadCsv(rows, `${event.value?.display_name || event.value?.name || 'event'}_3kdarts.csv`, false)
}

const exportBadartsDartConnectXlsx = async () => {
    if (!badartsRoster.value.length) { toast.info('Aucun joueur inscrit.'); return }
    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet('Player Roster ')
    sheet.addRow(['Org Player ID', 'Gender', 'ISO Country Code', 'EmailAddress', 'First Name', 'Last Name', 'Division (leagues Only)'])
    for (const p of badartsRoster.value) {
        const { firstname, name } = badartsPlayerName(p)
        sheet.addRow(['', '', 'FRA', '', firstname, name, ''])
    }
    await downloadXlsx(workbook, `${event.value?.display_name || event.value?.name || 'event'}_dartconnect.xlsx`)
}

const exportBadartsDartUpIndividualXlsx = async () => {
    if (badartsMode.value !== 'single') { toast.info('Aucun joueur en individuel (tournoi en mode double).'); return }
    if (!badartsRoster.value.length) { toast.info('Aucun joueur inscrit.'); return }
    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet('Participants')
    sheet.addRow(DARTUP_INDIVIDUAL_HEADER)
    for (const p of badartsRoster.value) {
        const { firstname, name } = badartsPlayerName(p)
        sheet.addRow([name, firstname, '', '', '', '', '', '', '', p.paid ? 'Oui' : 'Non', 'Oui'])
    }
    await downloadXlsx(workbook, `${event.value?.display_name || event.value?.name || 'event'}_dartup_individuel.xlsx`)
}

const exportBadartsDartUpDoublesXlsx = async () => {
    if (badartsMode.value !== 'double') { toast.info('Aucune doublette (tournoi en mode individuel).'); return }
    if (!badartsPairs.value.length) { toast.info('Aucune doublette inscrite.'); return }
    const { default: ExcelJS } = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet('Sheet')
    sheet.addRow(['Nom1', 'Prénom1', 'Email1', 'Nom2', 'Prénom2', 'Email2', "Nom d'équipe"])
    for (const [p1, p2] of badartsPairs.value) {
        const a = badartsPlayerName(p1)
        const b = badartsPlayerName(p2)
        sheet.addRow([a.name, a.firstname, '', b.name, b.firstname, '', ''])
    }
    await downloadXlsx(workbook, `${event.value?.display_name || event.value?.name || 'event'}_dartup_doubles.xlsx`)
}

const downloadCsv = (rows: string[], filename: string, withBom = true) => {
    const csv = (withBom ? '﻿' : '') + rows.join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
}

const downloadXlsx = async (workbook: any, filename: string) => {
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
}

const STATUS_LABELS: Record<string, string> = {
    open: 'Ouvert à tous',
    licensed_only: 'Licenciés uniquement',
    closed: 'Fermé',
}

// Ouvrir (open/licensed_only) exige au moins un mode de paiement réellement disponible
// (en ligne avec HelloAsso configuré, ou sur place) -- cf.
// events/routes.py::_check_registration_opening_allowed côté back. Fermer reste
// toujours possible, même sans configuration.
const REGISTRATION_STATUS_OPTIONS: { value: string; icon: string; label: string; requiresPaymentMode: boolean }[] = [
    { value: 'open', icon: '🟢', label: 'Ouvert à tous', requiresPaymentMode: true },
    { value: 'licensed_only', icon: '🟡', label: 'Licenciés uniquement', requiresPaymentMode: true },
    { value: 'closed', icon: '🔴', label: 'Fermé', requiresPaymentMode: false },
]
// "Licenciés uniquement" n'a pas de sens pour un tournoi local, qui ne vérifie plus
// aucune licence ; à l'inverse une coupe ne peut jamais être "Ouvert à tous" (règlement
// FFD, cf. events/routes.py::is_cup_event).
const visibleStatusOptions = computed(() => {
    if (isLocal.value) return REGISTRATION_STATUS_OPTIONS.filter(opt => opt.value !== 'licensed_only')
    if (event.value?.cup_scope) return REGISTRATION_STATUS_OPTIONS.filter(opt => opt.value !== 'open')
    return REGISTRATION_STATUS_OPTIONS
})

const setRegistrationStatus = async (status: string) => {
    togglingRegistrations.value = true
    try {
        await backendApi.patch(`/events/${eventId.value}/inscription-settings`, { registration_status: status }, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        event.value.registration_status = status
        toast.success(`Statut mis à jour : ${STATUS_LABELS[status] ?? status}`)
    } catch (err) {
        handleError(err, 'updating registration status')
    } finally {
        togglingRegistrations.value = false
    }
}

// Modes de paiement acceptés : enregistrés immédiatement, comme le statut. Le back
// refuse de retirer le dernier mode disponible d'un tournoi ouvert.
const savingPaymentModes = ref(false)
const setPaymentMode = async (field: 'accept_online_payment' | 'accept_onsite_payment', e: Event) => {
    const checkbox = e.target as HTMLInputElement
    const value = checkbox.checked
    savingPaymentModes.value = true
    try {
        const { data } = await backendApi.patch(`/events/${eventId.value}/inscription-settings`, { [field]: value }, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        for (const key of ['accept_online_payment', 'accept_onsite_payment', 'online_payment_available',
            'onsite_payment_available', 'registration_open'] as const) {
            event.value[key] = data[key]
        }
        toast.success('Modes de paiement mis à jour')
    } catch (err) {
        handleError(err, 'updating payment modes')
        // La case a déjà basculé visuellement : on la remet sur la valeur enregistrée.
        checkbox.checked = !value
    } finally {
        savingPaymentModes.value = false
    }
}

// Butoir de clôture automatique. L'input datetime-local travaille en heure locale du
// navigateur sans fuseau ("2026-10-12T18:00") : on convertit en ISO UTC à l'envoi, et
// dans l'autre sens à l'affichage, pour que 18:00 saisi soit bien 18:00 heure française.
const toLocalInputValue = (iso: string | null | undefined): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const deadlineForm = ref('')
const deadlineInitial = ref('')
const savingDeadline = ref(false)
watch(() => event.value?.registration_deadline, (iso) => {
    deadlineInitial.value = toLocalInputValue(iso)
    deadlineForm.value = deadlineInitial.value
}, { immediate: true })

const patchDeadline = async (value: string | null) => {
    savingDeadline.value = true
    try {
        const { data } = await backendApi.patch(`/events/${eventId.value}/inscription-settings`, { registration_deadline: value }, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        event.value.registration_deadline = data.registration_deadline
        event.value.registration_deadline_passed = data.registration_deadline_passed
        toast.success(value ? `Clôture automatique le ${formatDeadline(data.registration_deadline)}` : 'Clôture automatique retirée')
    } catch (err) {
        handleError(err, 'updating registration deadline')
    } finally {
        savingDeadline.value = false
    }
}

const saveDeadline = async () => {
    if (!deadlineForm.value) {
        await clearDeadline()
        return
    }
    const d = new Date(deadlineForm.value)
    if (isNaN(d.getTime())) {
        toast.error('Date limite invalide')
        return
    }
    await patchDeadline(d.toISOString())
}

const clearDeadline = async () => {
    await patchDeadline(null)
}

// price_cents/price_cents_unlicensed du côté back n'ont jamais eu de champ d'édition
// nulle part (ni ici ni dans LSEF, qui crée l'événement mais pas ses tarifs) -- au
// club organisateur de les fixer lui-même, comme le reste des réglages d'inscription.
const savingPricing = ref(false)
const pricingForm = ref({ licensed: 0, unlicensed: 0, team_pricing: false, team_licensed: 0, team_unlicensed: 0 })

// Tournoi local : réglable même sans premium, comme ses infos (cf. canEditLocalInfo).
const canEditPricing = computed(() => (isLocal.value ? canEditLocalInfo.value : canManage.value))
// Le mode "prix par équipe" n'a de sens que s'il y a des catégories doubles.
const hasDoubles = computed(() => !event.value?.format || event.value.format.includes('double'))

const fillPricingForm = () => {
    const e = event.value
    // Sans prix d'équipe encore saisi, pré-remplit avec l'équivalent du tarif actuel
    // (2 joueurs) pour que basculer en mode équipe ne change rien tant qu'on n'y touche pas.
    const licensed = (e?.price_cents ?? 0) / 100
    const unlicensed = (e?.price_cents_unlicensed ?? e?.price_cents ?? 0) / 100
    const teamLicensed = e?.price_team_cents != null ? e.price_team_cents / 100 : licensed * 2
    pricingForm.value = {
        licensed,
        unlicensed,
        team_pricing: !!e?.team_pricing,
        team_licensed: teamLicensed,
        team_unlicensed: e?.price_team_cents_unlicensed != null
            ? e.price_team_cents_unlicensed / 100
            : (e?.price_team_cents != null ? teamLicensed : unlicensed * 2),
    }
}

const savePricing = async () => {
    savingPricing.value = true
    try {
        // Un tournoi local n'a qu'un seul tarif -- on n'envoie/n'écrase pas
        // price_cents_unlicensed, sans effet de toute façon pour ce type d'événement
        // (cf. events/routes.py::_rate_cents_for), pour ne pas laisser une valeur
        // trompeuse si l'événement redevenait un jour officiel.
        const f = pricingForm.value
        const payload: Record<string, number | boolean> = {
            price_cents: Math.round(f.licensed * 100),
            team_pricing: f.team_pricing,
        }
        if (!singleRate.value) payload.price_cents_unlicensed = Math.round(f.unlicensed * 100)
        if (f.team_pricing) {
            payload.price_team_cents = Math.round(f.team_licensed * 100)
            if (!singleRate.value) payload.price_team_cents_unlicensed = Math.round(f.team_unlicensed * 100)
        }
        // Même endpoint que les infos pour un tournoi local (réglable sans premium).
        const url = isLocal.value ? `/events/local/${eventId.value}` : `/events/${eventId.value}/inscription-settings`
        const { data } = await backendApi.patch(url, payload, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        event.value.price_cents = data.price_cents
        event.value.price_cents_unlicensed = data.price_cents_unlicensed
        event.value.team_pricing = data.team_pricing
        event.value.price_team_cents = data.price_team_cents
        event.value.price_team_cents_unlicensed = data.price_team_cents_unlicensed
        fillPricingForm()
        toast.success('Tarifs mis à jour.')
    } catch (err) {
        handleError(err, 'updating event pricing')
    } finally {
        savingPricing.value = false
    }
}

const pricingSummary = computed(() => {
    if (!event.value) return ''
    const price = formatPrice(event.value)
    if (!price) return 'Aucun tarif'
    return eventTeamPrices.value ? price : `Par joueur · ${price}`
})
const registrationSummary = computed(() => {
    if (!event.value) return ''
    if (deadlinePassed(event.value)) return '🔒 Clôturées (date limite passée)'
    const opt = REGISTRATION_STATUS_OPTIONS.find(o => o.value === (event.value.registration_status ?? 'open'))
    return opt ? `${opt.icon} ${opt.label}` : ''
})
const paymentSummary = computed(() => {
    const e = event.value
    if (!e) return ''
    const modes = [
        e.accept_online_payment && e.helloasso_configured ? 'En ligne' : null,
        e.accept_onsite_payment ? 'Sur place' : null,
    ].filter(Boolean)
    return modes.length ? modes.join(' · ') : '⚠️ Aucun mode de paiement'
})
const openCategoriesSummary = computed(() => {
    const open = ALL_CATEGORIES.filter(cat => openCategories.value[cat.value]).length
    return open === ALL_CATEGORIES.length ? 'Toutes' : `${open} / ${ALL_CATEGORIES.length}`
})
const limitsSummary = computed(() => {
    const n = Object.values(categoryLimits.value).filter(v => v != null && (v as unknown) !== '').length
    return n ? `${n} limite${n > 1 ? 's' : ''}` : 'Aucune limite'
})

const getCategoryCount = (cat: typeof ALL_CATEGORIES[number]): number => {
    return getByCategory(cat).length
}

const saveCategoryLimits = async () => {
    savingLimits.value = true
    try {
        const limits: Record<string, number> = {}
        for (const [key, val] of Object.entries(categoryLimits.value)) {
            if (val != null && val > 0) limits[key] = val
        }
        // PATCH /events/{id} (route générique réservée comité/ligue/FFD) n'a pas
        // category_limits dans son schéma : le club organisateur (qui gère ses propres
        // limites) n'a de toute façon pas le droit d'appeler cette route. La bonne route
        // est inscription-settings, comme pour le tarif ci-dessous.
        const { data } = await backendApi.patch(`/events/${eventId.value}/inscription-settings`,
            { category_limits: Object.keys(limits).length ? limits : null }, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        event.value.category_limits = data.category_limits
        toast.success('Limites enregistrées.')
    } catch (err) {
        handleError(err, 'saving category limits')
    } finally {
        savingLimits.value = false
    }
}

const saveOpenCategories = async () => {
    savingOpenCategories.value = true
    try {
        const selected = ALL_CATEGORIES.map(c => c.value).filter(v => openCategories.value[v])
        // Tout coché = comportement par défaut (toutes ouvertes) -- on envoie null plutôt
        // que la liste complète, pour rester cohérent avec un tournoi qui n'a jamais été
        // restreint (et continuer à profiter de toute future catégorie ajoutée).
        const value = selected.length === ALL_CATEGORIES.length ? null : selected
        const { data } = await backendApi.patch(`/events/${eventId.value}/inscription-settings`,
            { open_categories: value }, {
            headers: { Authorization: `Bearer ${authStore.token}` },
        })
        event.value.open_categories = data.open_categories
        toast.success('Catégories ouvertes mises à jour.')
    } catch (err) {
        handleError(err, 'saving open categories')
    } finally {
        savingOpenCategories.value = false
    }
}

const addInscription = async () => {
    for (const [i, p] of adminPlayers.value.entries()) {
        if (!p.name.trim() || !p.firstname.trim()) {
            toast.error(`Joueur ${i + 1} : nom et prénom requis.`)
            return
        }
        if (!p.category_simple && !p.category_double) {
            toast.error(`Joueur ${i + 1} : sélectionnez au moins une catégorie.`)
            return
        }
        const blockedError = blockedSelectionError(p)
        if (blockedError) {
            toast.error(`Joueur ${i + 1} : ${blockedError}.`)
            return
        }
        const simpleCats = p.category_simple ? p.category_simple.split(',').map((c: string) => c.trim()) : []
        // Genre exigé pour Féminin (et la Double Féminine, plus bas), et pour Masculin
        // hors tournoi local (fermé aux joueuses, cf. eligibleSimpleCategories) ;
        // Vétéran/Junior sont déjà grisés sans genre pour un non-licencié (cf.
        // blockedSimpleCategories).
        if (simpleCats.includes('F') && !p.gender) {
            toast.error(`Joueur ${i + 1} : genre requis pour la catégorie Féminin.`)
            return
        }
        if (!isLocal.value && simpleCats.includes('M') && !p.gender) {
            toast.error(`Joueur ${i + 1} : genre requis pour la catégorie Masculin.`)
            return
        }
        // La date de naissance ne sert qu'à vérifier l'éligibilité Vétéran/Junior d'un
        // joueur non licencié (cf. _calculate_category côté backend) -- inutile de la
        // demander pour Mixte/Féminin, où seul le genre compte.
        if ((simpleCats.includes('V') || simpleCats.includes('J')) && !p.birth_date) {
            toast.error(`Joueur ${i + 1} : date de naissance requise pour vérifier l'éligibilité Vétéran/Junior.`)
            return
        }
        if (p.category_double && (!p.partner_name.trim() || !p.partner_firstname.trim())) {
            toast.error(`Joueur ${i + 1} : nom du partenaire obligatoire pour le double.`)
            return
        }
        if (p.category_double === 'DF' && (!p.gender || !p.partner_gender)) {
            toast.error(`Joueur ${i + 1} : genre du joueur et du partenaire requis pour la Double Féminine.`)
            return
        }
        if (adminHasDoubleDoublette(p) && (!p.partner_name_2.trim() || !p.partner_firstname_2.trim())) {
            toast.error(`Joueur ${i + 1} : nom du 2e partenaire obligatoire.`)
            return
        }
    }
    submitting.value = true
    try {
        // Ajout manuel = exception admin, hors HelloAsso, considérée payée sous la
        // responsabilité de qui l'ajoute (cf. inscriptions/routes.py::manual_register_inscriptions).
        const { data } = await backendApi.post('/inscriptions/manual-register', {
            event_id: eventId.value,
            registrations: adminPlayers.value.map(p => ({
                name: normalizeName(p.name),
                firstname: normalizeFirstname(p.firstname),
                is_licensed: p.is_licensed,
                birth_date: p.birth_date || null,
                gender: p.gender || null,
                category_simple: p.category_simple || null,
                category_double: p.category_double || null,
                partner_name: p.category_double ? normalizeName(p.partner_name) || null : null,
                partner_firstname: p.category_double ? normalizeFirstname(p.partner_firstname) || null : null,
                partner_is_licensed: p.category_double ? p.partner_is_licensed : null,
                partner_gender: p.category_double ? p.partner_gender || null : null,
                partner_name_2: adminHasDoubleDoublette(p) ? normalizeName(p.partner_name_2) || null : null,
                partner_firstname_2: adminHasDoubleDoublette(p) ? normalizeFirstname(p.partner_firstname_2) || null : null,
                partner_is_licensed_2: adminHasDoubleDoublette(p) ? p.partner_is_licensed_2 : null,
            }))
        }, { headers: { Authorization: `Bearer ${authStore.token}` } })

        toast.success(`${data.length} joueur(s) inscrits.`)
        adminPlayers.value = [createEmptyAdminPlayer()]
        showAddForm.value = false
        await Promise.all([fetchInscriptions(), fetchAdminInscriptionPayments()])
    } catch (err) {
        handleError(err, 'adding inscription')
    } finally {
        submitting.value = false
    }
}

// watch (immediate) plutôt que onMounted : la route /event-inscriptions/:eventId reste
// sur le même composant en changeant seulement le param quand on bascule d'un événement
// géré à un autre depuis le menu "Gérer mes événements" -- Vue Router ne redémonte pas
// le composant dans ce cas, donc onMounted seul ne rechargeait jamais les nouvelles
// données (la page restait figée sur le premier événement ouvert).
watch(eventId, async () => {
    loadPointageKind()
    loading.value = true
    try {
        // L'événement d'abord : sans premium (canManage faux), inscriptions et paiements
        // répondraient 402 -- on ne les demande que si on a le droit de les voir, et on
        // force l'onglet Paramètres, seul accessible dans ce cas.
        await fetchEvent()
        if (canManage.value) {
            await Promise.all([fetchInscriptions(), fetchAdminInscriptionPayments()])
        } else {
            activeTab.value = 'parametres'
        }
        if (canEditLocalInfo.value) await ensureClubOptionsLoaded()
    } finally {
        loading.value = false
    }
}, { immediate: true })
</script>

<style scoped>
.event-banner {
    background: var(--color-accent-bis, #1a3a5c);
    color: var(--color-bg, #fff);
    padding: 1rem 1.5rem;
    border-radius: var(--radius, 8px);
    margin-bottom: 1.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    align-items: center;
}

.event-banner p {
    margin: 0;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.tabs-bar {
    display: flex;
    gap: 0.5rem;
    border-bottom: 1px solid var(--color-light-shadow, #ddd);
    margin-bottom: 1.25rem;
}

.tab-btn {
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    padding: 0.7rem 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-fg-darker, #888);
    cursor: pointer;
}

.tab-btn:hover {
    color: var(--color-fg, #333);
}

.tab-btn.active {
    color: var(--color-accent, #1a3a5c);
    border-bottom-color: var(--color-accent, #1a3a5c);
}

.stats-section {
    margin-bottom: 1.25rem;
}

.stats-section-label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-fg-darker, #888);
    margin: 0 0 0.4rem 0;
}

.stats-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 0;
}

.stat-card {
    background: var(--color-light-shadow, #f5f5f5);
    border-radius: var(--radius, 8px);
    padding: 0.6rem 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 80px;
}

.stat-card.grand-total {
    background: var(--color-accent, #1a3a5c);
    color: var(--color-bg, #fff);
}

.stat-label {
    font-size: 0.78rem;
    color: inherit;
    opacity: 0.85;
}

.stat-value {
    font-size: 1.4rem;
    font-weight: 700;
}

.checkin-bar {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.pointage-scope {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1rem;
    margin-bottom: 1rem;
}

.pointage-scope-label {
    font-weight: 600;
}

.pointage-scope-presets {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.8rem;
    align-items: center;
}

.pointage-filter {
    width: auto;
    flex: 0 0 auto;
}

.checkin-cat {
    margin: 0.1rem 0.25rem 0.1rem 0;
    padding: 0.15rem 0.5rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: 999px;
    font-size: 0.8rem;
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    cursor: pointer;
    white-space: nowrap;
}

.checkin-cat.checked {
    border-color: #2e9d57;
    background: color-mix(in srgb, #2e9d57 18%, var(--color-bg, #fff));
    font-weight: 600;
}

.checkin-cat.out-of-scope {
    opacity: 0.45;
}

.checkin-cat:disabled {
    cursor: default;
}

.checkin-counter {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-fg-darker, #555);
}

.onsite-col {
    white-space: nowrap;
}

.btn-collect,
.onsite-paid-badge {
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

.btn-collect {
    border: 1px solid #d68910;
    background: color-mix(in srgb, #d68910 15%, var(--color-bg, #fff));
    color: var(--color-fg, #333);
}

.onsite-paid-badge {
    border: 1px solid #2e9d57;
    background: color-mix(in srgb, #2e9d57 18%, var(--color-bg, #fff));
    color: var(--color-fg, #333);
}

.btn-collect:disabled,
.onsite-paid-badge:disabled {
    cursor: default;
    opacity: 0.7;
}

.checkin-col {
    width: 56px;
    text-align: center;
}

.checkin-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.3rem;
    padding: 0;
    line-height: 1;
    transition: transform 0.1s;
}

.checkin-btn:hover {
    transform: scale(1.2);
}

tr.row-checkedin {
    opacity: 0.55;
}

.filters {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
    align-items: center;
}

.search-input {
    flex: 1;
    min-width: 200px;
    padding: 0.5rem 0.8rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    font-size: 0.95rem;
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
}

.category-section {
    margin-bottom: 1rem;
}

table th {
    padding: 2px 6px;
}

table {
    border-collapse: collapse;
    font-size: 0.88rem;
    line-height: 1;
}

table td {
    padding: 0 6px;
    line-height: 1.5;
}

table button {
    line-height: 1;
    font-size: 0.85rem;
    padding: 0 2px;
    height: 20px;
}

.category-title {
    font-size: 1.05rem;
    color: var(--color-accent, #1a3a5c);
    border-bottom: 2px solid var(--color-accent, #1a3a5c);
    padding-bottom: 0.3rem;
    margin-bottom: 0.75rem;
    cursor: pointer;
    user-select: none;
}

.cat-toggle {
    font-size: 0.75rem;
    margin-left: 0.5rem;
    opacity: 0.6;
}

.cat-count {
    font-size: 0.9rem;
    font-weight: 400;
    color: var(--color-fg-darker, #666);
}

.export-bar {
    margin-bottom: 1rem;
}

.btn-export-all {
    padding: 0.4rem 1rem;
    font-size: 0.9rem;
    font-weight: 600;
    background: transparent;
    border: 1px solid var(--color-accent, #1a3a5c);
    color: var(--color-accent, #1a3a5c);
    border-radius: 4px;
    cursor: pointer;
}

.btn-export-all:hover {
    background: var(--color-accent, #1a3a5c);
    color: var(--color-bg, #fff);
}

.export-modal-content {
    text-align: left;
}

.export-modal-content h3 {
    margin: 0 0 1rem;
    color: var(--color-accent, #1a3a5c);
}

.export-modal-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--color-fg-darker, #888);
    margin: 0 0 0.5rem;
}

.export-software-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.6rem;
    margin-bottom: 1.2rem;
}

@media (max-width: 480px) {
    .export-software-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.export-software-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    padding: 0.8rem 0.5rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 8px);
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    cursor: pointer;
}

.export-software-card.active {
    border-color: var(--color-accent, #1a3a5c);
    background: color-mix(in srgb, var(--color-accent, #1a3a5c) 12%, var(--color-bg, #fff));
}

.export-software-logo {
    height: 32px;
    max-width: 100%;
    object-fit: contain;
}

.export-software-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-light-shadow, #eee);
    color: var(--color-fg, #333);
    font-size: 0.75rem;
    font-weight: 700;
}

.export-software-name {
    font-size: 0.78rem;
    text-align: center;
    font-weight: 600;
}

.export-category-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.export-category-btn {
    padding: 0.5rem 0.9rem;
    font-size: 0.88rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    cursor: pointer;
}

.export-category-btn.active {
    border-color: var(--color-accent, #1a3a5c);
    background: var(--color-accent, #1a3a5c);
    color: var(--color-bg, #fff);
}

.export-modal-hint {
    font-size: 0.82rem;
    color: var(--color-fg-darker, #888);
    margin: 0 0 1rem;
}

.players-list-cell {
    font-size: 0.82rem;
    color: var(--color-fg-darker, #444);
    max-width: 260px;
    line-height: 1.5;
}

.player-inline {
    white-space: nowrap;
}

.cat-hint {
    color: var(--color-fg-darker, #888);
    font-size: 0.78rem;
}

.checkin-toggle {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
    color: inherit;
    text-align: left;
}

.row-warning {
    cursor: help;
}

.checkin-toggle:disabled {
    cursor: default;
}

.category-change-select {
    width: auto;
    margin-top: 0;
    font-size: 0.9rem;
}

.actions-cell {
    white-space: nowrap;
    display: flex;
    gap: 0.4rem;
    align-items: center;
    flex-wrap: wrap;
}

.partial-amount-input {
    width: 60px;
    margin-top: 0;
    padding: 0.2rem 0.4rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: 4px;
    font-size: 0.85rem;
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
}



.payments-table {
    width: 100%;
    border-collapse: collapse;
}

.table-wrapper {
    overflow-x: auto;
}

.mobile-cards {
    display: none;
}

@media (max-width: 640px) {
    .table-wrapper {
        display: none;
    }

    .mobile-cards {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        margin-top: 0.5rem;
    }
}

.mobile-card {
    background: var(--color-bg, #fff);
    border: 1px solid var(--color-light-shadow, #e5e7eb);
    border-radius: 8px;
    padding: 0.75rem 0.875rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.mobile-card .card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
}

.mobile-card .card-name {
    font-weight: 700;
    font-size: 0.9rem;
}

.mobile-card .card-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 0.4rem;
    border-top: 1px solid var(--color-light-shadow, #eee);
}

.price-hint {
    margin-left: 0.4rem;
    font-weight: 700;
    font-size: 0.85rem;
    color: var(--color-error, #991b1b);
}

.action-btn {
    background: transparent;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: 4px;
    padding: 0.2rem 0.5rem;
    cursor: pointer;
    font-size: 0.9rem;
    line-height: 1.4;
    transition: background 0.15s;
}

.action-btn:hover {
    background: var(--color-light-shadow, #f0f0f0);
}

.global-search-input {
    display: block;
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.8rem;
    font-size: 0.95rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    width: 100%;
    max-width: 360px;
}

.no-data {
    color: var(--color-fg-darker, #888);
    font-size: 0.9rem;
    font-style: italic;
    padding: 0.5rem 0;
}

.payment-section {
    margin-top: 2rem;
}

.global-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--color-light-shadow, #eee);
}

/* ── Limites par catégorie ── */
.presentation-hint {
    margin: 0 0 0.75rem;
    font-size: 0.85rem;
    color: var(--color-fg-darker, #888);
}

.affiche-preview {
    display: block;
    max-width: 180px;
    max-height: 240px;
    margin-bottom: 0.5rem;
    border-radius: var(--radius, 6px);
    border: 1px solid var(--color-light-shadow, #ccc);
}




.btn-save-limits {
    padding: 0.2rem 0.65rem;
    font-size: 0.8rem;
    font-weight: 600;
    background: transparent;
    border: 1px solid var(--color-accent, #1a3a5c);
    color: var(--color-accent, #1a3a5c);
    border-radius: 4px;
    cursor: pointer;
}

.btn-save-limits:hover:not(:disabled) {
    background: var(--color-accent, #1a3a5c);
    color: var(--color-bg, #fff);
}

.btn-save-limits:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.open-categories-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem 1.2rem;
    font-size: 0.85rem;
}

.open-cat-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--color-fg-darker, #555);
    cursor: pointer;
}

.limits-grid {
    display: grid;
    grid-template-columns: max-content auto auto auto;
    gap: 0.3rem 0.6rem;
    align-items: center;
    font-size: 0.85rem;
}

.limit-row {
    display: contents;
}

.limit-label {
    color: var(--color-fg-darker, #555);
    padding-right: 0.5rem;
}

.limit-count {
    font-weight: 600;
    text-align: right;
    color: var(--color-fg, #333);
}

.limit-sep {
    color: var(--color-fg-darker, #ccc);
    text-align: center;
}

.limit-input {
    width: 44px;
    padding: 0.15rem 0.3rem;
    font-size: 0.85rem;
    text-align: center;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: 4px;
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
}

.limit-input:focus {
    outline: none;
    border-color: var(--color-accent, #1a3a5c);
}

.limit-input-readonly {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-style: dashed;
    color: var(--color-fg-muted, #888);
    background: transparent;
}

.registrant-selector {
    position: relative;
}

.registrant-dropdown {
    position: absolute;
    z-index: 100;
    background: var(--color-bg, #fff);
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.registrant-option {
    padding: 0.5rem 0.8rem;
    cursor: pointer;
    font-size: 0.9rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.registrant-option:hover {
    background: var(--color-light-shadow, #f5f5f5);
}

.registrant-nick {
    color: var(--color-fg-darker, #888);
    font-size: 0.82rem;
}

.add-form {
    margin-top: 1rem;
}

.add-form>h4 {
    margin-bottom: 1rem;
    color: var(--color-accent, #1a3a5c);
}

.player-entry {
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    padding: 1.2rem;
    margin-bottom: 1.2rem;
    background: var(--color-bg, #fff);
}

.player-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.player-entry-header h4 {
    margin: 0;
    color: var(--color-accent, #1a3a5c);
}

.btn-remove {
    background: transparent;
    border: 1px solid var(--color-error, #e74c3c);
    color: var(--color-error, #e74c3c);
    padding: 0.3rem 0.8rem;
    border-radius: var(--radius, 6px);
    cursor: pointer;
    font-size: 0.85rem;
}

.doublette-group {
    background: var(--color-light-shadow, #f5f5f5);
    padding: 0.8rem;
    border-radius: var(--radius, 6px);
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    margin: 0.4rem 0 0.8rem;
    cursor: pointer;
}

.add-form-hint {
    font-size: 0.85rem;
    color: var(--color-fg-darker, #666);
    margin: 0 0 1rem;
}

.player-price {
    margin-top: 0.5rem;
    font-size: 0.95rem;
    color: var(--color-accent, #1a3a5c);
    text-align: right;
}

.btn-add-player {
    width: 100%;
    padding: 0.7rem;
    border: 2px dashed var(--color-light-shadow, #ccc);
    background: transparent;
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 1rem;
    color: var(--color-accent, #1a3a5c);
    margin-bottom: 1.2rem;
    transition: border-color 0.2s;
}

.btn-add-player:hover {
    border-color: var(--color-accent, #1a3a5c);
}

.total-price {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.1rem;
    padding: 0.8rem 1rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    margin-bottom: 1.2rem;
}

.total-price strong {
    font-size: 1.3rem;
    color: var(--color-accent, #1a3a5c);
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-group {
    margin-bottom: 0.9rem;
}

.form-group label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
}

.form-input {
    display: block;
    width: 100%;
    padding: 0.5rem 0.7rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    font-size: 0.95rem;
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    box-sizing: border-box;
}


.form-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 1rem;
}

.btn-submit {
    padding: 0.6rem 1.4rem;
    background: var(--color-accent, #1a3a5c);
    color: var(--color-bg, #fff);
    border: none;
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 600;
}

.btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    padding: 0.6rem 1.2rem;
    background: transparent;
    border: 1px solid var(--color-light-shadow);
    color: var(--color-fg, #333);
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.9rem;
}

.btn-secondary:hover:not(:disabled) {
    border-color: var(--color-accent);
}

.btn-danger {
    padding: 0.6rem 1.2rem;
    background: transparent;
    border: 1px solid var(--color-error, #e74c3c);
    color: var(--color-error, #e74c3c);
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
}

.btn-danger:hover {
    background: color-mix(in srgb, var(--color-error, #e74c3c) 15%, var(--color-bg-lighter, #fff));
}

.share-link-control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    flex-wrap: wrap;
    width: 100%;
}

.share-link-input {
    flex: 1 1 260px;
    min-width: 0;
    margin: 0;
    font-size: 0.85rem;
}

.share-link-open {
    color: var(--color-accent);
    font-weight: 600;
    font-size: 0.85rem;
    text-decoration: none;
}

.share-link-open:hover {
    text-decoration: underline;
}

.premium-banner {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    padding: 1rem 1.25rem;
    border-radius: var(--radius, 8px);
    background: rgba(245, 165, 36, 0.12);
    border: 1px solid var(--color-accent, #f5a524);
    color: var(--color-fg, #333);
}

.premium-banner-icon {
    font-size: 1.6rem;
    line-height: 1;
}

.premium-banner-body {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.premium-banner-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 800;
    color: var(--color-accent, #f5a524);
}

.premium-banner-text {
    margin: 0;
    font-size: 0.9rem;
}

.premium-banner-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.25rem;
}

.premium-banner-cta {
    align-self: flex-start;
    padding: 0.45rem 1rem;
    border-radius: var(--radius, 8px);
    background: var(--color-accent, #f5a524);
    color: var(--color-bg, #fff);
    font-weight: 700;
    font-size: 0.85rem;
    text-decoration: none;
}

.info-banner {
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    border-radius: var(--radius, 8px);
    background: var(--color-bg-soft, rgba(0, 0, 0, 0.04));
    border: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
    font-size: 0.85rem;
    color: var(--text-muted, #666);
}

.registration-status-control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    flex-wrap: wrap;
}

.payment-modes-control .checkbox-label {
    margin: 0;
}

.status-buttons {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.status-btn {
    padding: 0.5rem 0.8rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    font-size: 0.85rem;
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
}

.status-btn:hover:not(:disabled) {
    border-color: var(--color-accent);
}

.status-btn.active {
    border-color: var(--color-accent);
    background: color-mix(in srgb, var(--color-accent) 15%, var(--color-bg, #fff));
    font-weight: 600;
}

.status-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.helloasso-missing-hint {
    width: 100%;
    margin: 0.3rem 0 0;
    font-size: 0.8rem;
    color: var(--color-fg-darker, #888);
}

.helloasso-missing-hint a {
    color: var(--color-accent);
    font-weight: 600;
}

.deadline-control {
    margin-top: 0.6rem;
}

.deadline-input {
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
    font-size: 0.85rem;
}

.deadline-passed {
    color: var(--color-error, #c0392b);
    font-weight: 600;
}


.pricing-row {
    display: flex;
    align-items: center;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
}

.settings-sections {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
}

.settings-section {
    border: 1px solid var(--color-light-shadow, #e5e5e5);
    border-radius: var(--radius, 6px);
    background: var(--color-bg, #fff);
}

.settings-summary {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    list-style: none;
    user-select: none;
}

.settings-summary::-webkit-details-marker {
    display: none;
}

.settings-summary::after {
    content: '▶';
    margin-left: auto;
    flex-shrink: 0;
    font-size: 0.7rem;
    color: var(--color-fg-darker, #888);
    transition: transform 0.15s;
}

.settings-section[open] > .settings-summary::after {
    transform: rotate(90deg);
}

.settings-summary:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: -2px;
    border-radius: var(--radius, 6px);
}

.settings-title {
    flex-shrink: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-fg, #333);
}

.settings-hint {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.85rem;
    color: var(--color-fg-darker, #888);
}

.settings-section[open] > .settings-summary {
    border-bottom: 1px solid var(--color-light-shadow, #e5e5e5);
}

.settings-body {
    padding: 1rem;
}

.settings-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
}

.settings-banner {
    margin: 0.25rem 0;
}

.pricing-row-title {
    flex-basis: 100%;
    font-weight: 600;
}

.pricing-mode {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    font-size: 0.9rem;
}

.pricing-mode-label {
    font-weight: 600;
}

.pricing-input {
    width: 5rem;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--color-light-shadow, #ccc);
    border-radius: var(--radius, 6px);
    background: var(--color-bg, #fff);
    color: var(--color-fg, #333);
}

.btn-warning {
    padding: 0.6rem 1.2rem;
    background: transparent;
    border: 1px solid var(--color-accent, #f9a825);
    color: var(--color-accent, #f9a825);
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
}

.btn-warning:hover {
    background: color-mix(in srgb, var(--color-accent, #f9a825) 15%, var(--color-bg-lighter, #fff));
}

.btn-success {
    padding: 0.6rem 1.2rem;
    background: transparent;
    border: 1px solid var(--color-success, #27ae60);
    color: var(--color-success, #27ae60);
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
}

.btn-success:hover {
    background: color-mix(in srgb, var(--color-success, #27ae60) 15%, var(--color-bg-lighter, #fff));
}

.centered-block {
    text-align: center;
    padding: 3rem 1rem;
}

@media (max-width: 600px) {
    .form-row {
        grid-template-columns: 1fr;
    }

    .inscriptions-table {
        font-size: 0.82rem;
    }

    .filters {
        flex-direction: column;
    }

    .global-actions {
        flex-direction: column;
    }
}

.payment-status {
    margin-left: 1rem;
    font-size: 0.9rem;
}

.partner-hint {
    font-size: 0.78rem;
    color: var(--color-fg-darker, #555);
    font-style: italic;
}

.clickable-row {
    cursor: pointer;
}

.clickable-row:hover {
    filter: brightness(0.96);
}

.checkin-indicator {
    font-size: 1.2rem;
    line-height: 1;
    /* --font-main (thème) ne contient pas les emojis -- sans repli explicite vers une
       police système capable de les afficher, ⬜/✅ tombe sur un glyphe de
       substitution (petit carré coloré illisible), cf. capture utilisateur. */
    font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
}


.modal-delete-btn {
    align-self: flex-start;
}
</style>